import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft, Check, Download, Loader } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api';
import { API_CONFIG } from '@/config';

// Default property types for Cleaning category
const DEFAULT_PROPERTY_TYPES = [
  { id: 1, label: 'Residential Property', icon: '🏘️', property_id: 'residential' },
  { id: 2, label: 'Commercial Office Property', icon: '🏢', property_id: 'commercial' },
  { id: 3, label: 'Airbnb / Serviced Accommodation & Hotels', icon: '🏨', property_id: 'airbnb' },
  { id: 4, label: 'HMO - House of Multiple Occupancy', icon: '🏚️', property_id: 'hmo' },
  { id: 5, label: 'Event Venue', icon: '🎪', property_id: 'event' }
];

// Airbnb turnaround service types
const AIRBNB_SERVICE_TYPES = [
  { id: 1, label: 'Standard Turnaround (Next Day Check-in)', icon: '📅', multiplier: 1.00, description: 'Regular schedule' },
  { id: 2, label: 'Express Turnaround (Same Day Check-in)', icon: '⚡', multiplier: 1.20, description: 'Quick turnaround' },
  { id: 3, label: 'Premium Turnaround (Luxury Listings)', icon: '✨', multiplier: 1.35, description: 'Luxury preparation' },
  { id: 4, label: 'Periodic Deep Clean', icon: '🧹', multiplier: 1.40, description: 'Deep cleaning' },
  { id: 5, label: 'After-Party / Heavy Soiling Clean (Recovery Clean)', icon: '🔄', multiplier: 1.55, description: 'Heavy cleaning' },
  { id: 6, label: 'Hotel Housekeeping Support', icon: '🏨', multiplier: 1.15, description: 'Hotel maintenance' }
];

// Floor area options with base pricing - UPDATED with corrected included_sqm values
const FLOOR_AREA_OPTIONS = [
  { value: 30, label: '<40 sqm (Small Studio/1BR)', price: 65, range: '<40', included_sqm: 40 },
  { value: 55, label: '40-70 sqm (Standard 1-2BR)', price: 85, range: '40-70', included_sqm: 70 },
  { value: 85, label: '70-100 sqm (Larger 2-3BR)', price: 115, range: '70-100', included_sqm: 100 },
  { value: 120, label: '100-140 sqm (Spacious 3-4BR)', price: 150, range: '100-140', included_sqm: 140 },
  { value: 170, label: '140-200 sqm (Large 4-5BR)', price: 190, range: '140-200', included_sqm: 180 },
  { value: 250, label: '200-300 sqm (Large House)', price: 240, range: '200-300', included_sqm: 220 },
  { value: 350, label: '300+ sqm (Luxury/Large Estate)', price: 350, range: '300+', included_sqm: 300 }
];

// Floor levels
const FLOOR_LEVEL_OPTIONS = [
  { id: '0-1', levels: '0-1', multiplier: 1.00, description: 'Single level' },
  { id: '2-3', levels: '2-3', multiplier: 1.08, description: 'Multiple levels with stairs' },
  { id: '4-6', levels: '4-6', multiplier: 1.15, description: 'Many levels and stairs' }
];

// Appliances with costs
const APPLIANCE_OPTIONS = [
  { id: 'oven', name: 'Oven', cost: 45, description: 'Deep clean inside & out' },
  { id: 'fridge', name: 'Fridge', cost: 25, description: 'Interior & shelves clean' },
  { id: 'dishwasher', name: 'Dishwasher', cost: 20, description: 'Interior clean & filter' },
  { id: 'microwave', name: 'Microwave', cost: 15, description: 'Interior steam clean' },
  { id: 'washer_dryer', name: 'Washer/Dryer', cost: 20, description: 'Exterior & filter clean' },
  { id: 'coffee_machine', name: 'Coffee Machine', cost: 15, description: 'Descale & clean' }
];

// Extra areas with costs
const EXTRA_AREAS_OPTIONS = [
  { id: 'balcony', name: 'Balcony', cost: 10, description: 'Balcony cleaning' },
  { id: 'garden_small', name: 'Garden (Small)', cost: 15, description: 'Basic garden tidy' },
  { id: 'garden_medium', name: 'Garden (Medium)', cost: 25, description: 'Medium garden tidy' },
  { id: 'garden_large', name: 'Garden (Large)', cost: 35, description: 'Extensive garden' },
  { id: 'hot_tub', name: 'Hot Tub', cost: 60, description: 'Hot tub clean & service' },
  { id: 'bbq', name: 'BBQ Area', cost: 17.50, description: 'BBQ cleaning' },
  { id: 'gym', name: 'Gym / Games Room', cost: 30, description: 'Equipment & floor clean' },
  { id: 'office', name: 'Office', cost: 8, description: 'Desk & furniture' }
];

// Laundry options
const LAUNDRY_OPTIONS = [
  { id: 'client_provides', name: 'Client Provides Linen', multiplier: 1.00, fixed_cost: 0, description: 'Guest-ready linen provided' },
  { id: 'on_site', name: 'On-Site Laundry', multiplier: 1.08, fixed_cost: 17.50, description: 'Laundry on property' },
  { id: 'off_site', name: 'Off-Site Laundry', multiplier: 1.15, fixed_cost: 31.50, description: 'External laundry service' },
  { id: 'linen_hire', name: 'Linen Hire Service', multiplier: 1.25, fixed_cost: 65.00, description: 'Professional linen service' }
];

// Treatment options
const TREATMENT_OPTIONS = [
  { id: 'limescale', name: 'Limescale Removal', multiplier: 1.10, description: 'Remove hard water deposits' },
  { id: 'grease', name: 'Heavy Grease Clean', multiplier: 1.12, description: 'Deep grease cleaning' },
  { id: 'mould', name: 'Mould Treatment', multiplier: 1.18, description: 'Mould remediation' }
];

// Parking options
const PARKING_OPTIONS = [
  { id: 'free_available', name: 'Free Parking Available', multiplier: 1.00, description: 'Free parking on site' },
  { id: 'limited_parking', name: 'Limited Parking', multiplier: 1.05, description: 'Limited parking spots' },
  { id: 'no_parking', name: 'No Parking', multiplier: 1.08, description: 'Street parking only' }
];

// Cleaning Cupboard options
const CLEANING_CUPBOARD_OPTIONS = [
  { id: 'fully_stocked', name: 'Fully Stocked Cleaning Cupboard', fixed_cost: 0, description: 'All cleaning supplies provided on-site' },
  { id: 'some_missing', name: 'Some Items Missing', fixed_cost: 10, description: 'Basic supplies available, some missing' },
  { id: 'no_cupboard', name: 'No Cleaning Cupboard', fixed_cost: 25, description: 'No supplies on-site, bring everything' }
];

// Waste sorting options
const WASTE_SORTING_OPTIONS = [
  { id: 'no_sorting', name: 'No Sorting Required', fixed_cost: 0, description: 'General waste only' },
  { id: 'basic', name: 'Basic Sorting', fixed_cost: 8.50, description: 'General + recycling' },
  { id: 'full', name: 'Full Sorting', fixed_cost: 17.50, description: 'General + recycling + glass/food' },
  { id: 'removal', name: 'Remove Waste Off-site', fixed_cost: 40, description: 'Complete waste removal' }
];

// Restocking options
const RESTOCKING_OPTIONS = [
  { id: 'none', name: 'No Restocking', multiplier: 1.00, fixed_cost: 0, description: 'No restocking required' },
  { id: 'basic', name: 'Basic Restocking', multiplier: 1.00, fixed_cost: 13, description: 'Toilet roll, hand soap' },
  { id: 'standard', name: 'Standard Restocking', multiplier: 1.00, fixed_cost: 25, description: 'Basic + bin bags, dish soap' },
  { id: 'full', name: 'Full Restocking Package', multiplier: 1.15, fixed_cost: 47.50, description: 'Full guest supplies' },
  { id: 'client_supplies', name: 'Client Supplies Stock', multiplier: 1.00, fixed_cost: 11.50, description: 'We place your stock only' }
];

// Number options for bathrooms, kitchens, beds
const NUMBER_OPTIONS = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10+' }
];

// Bedroom options (hours per room)
const BEDROOM_OPTIONS = [
  { value: 1, label: '1 bedroom', hours: 0.5 },
  { value: 2, label: '2 bedrooms', hours: 1.0 },
  { value: 3, label: '3 bedrooms', hours: 1.5 },
  { value: 4, label: '4 bedrooms', hours: 2.0 },
  { value: 5, label: '5 bedrooms', hours: 2.5 },
  { value: 6, label: '6+ bedrooms', hours: 3.0 }
];

// Reception room options (hours per room)
const RECEPTION_OPTIONS = [
  { value: 0, label: 'None', hours: 0 },
  { value: 1, label: '1 reception room', hours: 0.75 },
  { value: 2, label: '2 reception rooms', hours: 1.5 },
  { value: 3, label: '3+ reception rooms', hours: 2.25 }
];

// Furnishing level options (multiplier)
const FURNISHING_LEVEL_OPTIONS = [
  { id: 'unfurnished', label: 'Unfurnished', multiplier: 1.00, description: 'Empty property' },
  { id: 'part_furnished', label: 'Part Furnished', multiplier: 1.08, description: 'Some furniture present' },
  { id: 'furnished', label: 'Furnished', multiplier: 1.15, description: 'Fully furnished' },
  { id: 'luxury_furnished', label: 'Luxury Furnished', multiplier: 1.25, description: 'High-end furnishings' }
];

// Urgency options (multiplier)
const URGENCY_OPTIONS = [
  { id: 'standard', label: 'Standard (7+ days notice)', multiplier: 1.00, description: 'Regular scheduling' },
  { id: 'urgent', label: 'Urgent (2-6 days)', multiplier: 1.15, description: 'Quick turnaround' },
  { id: 'emergency', label: 'Emergency (Next day)', multiplier: 1.35, description: 'Immediate service' },
  { id: 'asap', label: 'ASAP (Same day)', multiplier: 1.50, description: 'Immediate availability' }
];

// Business rule options
const BUSINESS_RULE_OPTIONS = [
  { id: 'residue_count_3plus', label: 'Residue count ≥ 3 types', condition: 'residue_count >= 3', impact: '-10% confidence' },
  { id: 'site_still_active', label: 'Site activity: Still Active', condition: 'site_activity == "still_active"', impact: '-15% confidence' },
  { id: 'emergency_service', label: 'Emergency service requested', condition: 'urgency == "emergency" OR urgency == "asap"', impact: '-20% confidence' }
];

// Confidence rule thresholds
const CONFIDENCE_RULES = [
  { range: '<50%', tolerance: '±25%', description: 'Low confidence - wide pricing range' },
  { range: '50-69%', tolerance: '±20%', description: 'Medium confidence - standard range' },
  { range: '≥70%', tolerance: '±15%', description: 'High confidence - tight range' }
];

// EVENT VENUE SERVICE TYPES
const EVENT_VENUE_SERVICE_TYPES = [
  { id: 1, label: 'Pre-Event Services', icon: '🧹', description: 'Preparation and setup cleaning before event' },
  { id: 2, label: 'During-Event Services', icon: '🎪', description: 'Ongoing cleaning and maintenance during event' },
  { id: 3, label: 'Post-Event Services', icon: '✨', description: 'Cleanup and restoration after event' }
];

// EVENT VENUE QUESTIONS
const EVENT_VENUE_QUESTIONS = [
  { id: 'event_type', label: 'Please confirm the event type', type: 'select', options: [
    { id: 'conference', label: 'Conference' },
    { id: 'gala', label: 'Gala' },
    { id: 'wedding', label: 'Wedding' },
    { id: 'corporate', label: 'Corporate Event' },
    { id: 'private', label: 'Private Event' }
  ], required: true },
  
  { id: 'venue_capacity', label: 'Please confirm the venue\'s maximum capacity', type: 'select', options: [
    { id: '<100', label: 'Under 100 people' },
    { id: '100-250', label: '100–250 people' },
    { id: '250-500', label: '250–500 people' },
    { id: '500+', label: '500+ people' }
  ], required: true },
  
  { id: 'drinks_service', label: 'Please confirm the arranged drinks service', type: 'select', options: [
    { id: 'alcoholic', label: 'Yes (alcoholic)' },
    { id: 'soft_only', label: 'Soft only' },
    { id: 'none', label: 'None' }
  ], required: true },
  
  { id: 'items_used', label: 'Will any of the following items be used at the venue?', type: 'multi-select', options: [
    { id: 'confetti', label: 'Confetti' },
    { id: 'balloons', label: 'Balloons' },
    { id: 'pyrotechnics', label: 'Pyrotechnics' },
    { id: 'smoke_haze', label: 'Smoke/Haze' }
  ], required: false },
  
  { id: 'entry_type', label: 'Which best describes entry type to the event?', type: 'select', options: [
    { id: 'open', label: 'Open' },
    { id: 'ticketed', label: 'Ticketed' },
    { id: 'private', label: 'Private' }
  ], required: true },
  
  { id: 'areas_to_clean', label: 'Please select all the areas to be cleaned', type: 'multi-select', options: [
    { id: 'main_hall', label: 'Main hall' },
    { id: 'breakout_rooms', label: 'Breakout rooms' },
    { id: 'toilets', label: 'Toilets' },
    { id: 'kitchen', label: 'Kitchen' },
    { id: 'reception', label: 'Reception' },
    { id: 'outside', label: 'Outside areas' }
  ], required: true },
  
  { id: 'floor_type', label: 'Please select the general floor type at the venue', type: 'select', options: [
    { id: 'carpet', label: 'Carpet' },
    { id: 'wooden', label: 'Wooden' },
    { id: 'tile_hard', label: 'Tile/Hard flooring' },
    { id: 'mixed', label: 'Mixed' }
  ], required: true },
  
  { id: 'floor_levels', label: 'Please confirm the number of floor levels to be cleaned', type: 'select', options: [
    { id: '1', label: '1 level' },
    { id: '2-3', label: '2-3 levels' },
    { id: '4+', label: '4+ levels' }
  ], required: true },
  
  { id: 'toilet_attendant', label: 'Which best describes the toilet attendant requirements', type: 'select', options: [
    { id: 'during_only', label: 'During event only' },
    { id: 'before_during', label: 'Before & during' },
    { id: 'full_coverage', label: 'Full coverage' },
    { id: 'none', label: 'None' }
  ], required: true },
  
  { id: 'pre_event_access', label: 'Please confirm Pre-event Access Window For Cleaners', type: 'text', required: true },
  { id: 'post_event_access', label: 'Please confirm Post-event Access Window For Cleaners', type: 'text', required: true },
  { id: 'event_duration', label: 'Please confirm Event Duration', type: 'text', required: true },
  
  { id: 'requirement_level', label: 'Please which best describes your requirement', type: 'select', options: [
    { id: 'basic', label: 'Basic cleaning' },
    { id: 'standard', label: 'Standard cleaning' },
    { id: 'premium', label: 'Premium cleaning' }
  ], required: true },
  
  { id: 'overrun_probability', label: 'How likely is the event to overrun its planned end-time', type: 'select', options: [
    { id: 'unlikely', label: 'Unlikely' },
    { id: 'possible', label: 'Possible' },
    { id: 'likely', label: 'Likely' },
    { id: 'very_likely', label: 'Very likely' }
  ], required: true },
  
  { id: 'additional_services', label: 'Will any of the following be required?', type: 'multi-select', options: [
    { id: 'carpet_cleaning', label: 'Carpet cleaning' },
    { id: 'upholstery', label: 'Upholstery' },
    { id: 'window_cleaning', label: 'Window cleaning' },
    { id: 'waste_removal', label: 'Waste removal' }
  ], required: false },
  
  { id: 'floor_area', label: 'What is the approximate floor area size', type: 'select', options: [
    { id: '<500', label: 'Under 500 m²' },
    { id: '500-1000', label: '500–1,000 m²' },
    { id: '1000-2000', label: '1,000–2,000 m²' },
    { id: '2000+', label: '2,000+ m²' }
  ], required: true },
  
  { id: 'furniture_moving', label: 'Who moves/sets furniture', type: 'select', options: [
    { id: 'venue_staff', label: 'Venue staff' },
    { id: 'cleaners', label: 'Cleaners' },
    { id: 'event_organiser', label: 'Event organiser' },
    { id: 'other', label: 'Other' }
  ], required: true },
  
  { id: 'table_chair_handling', label: 'Do you require table and chair wiping and stacking?', type: 'select', options: [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' }
  ], required: true },
  
  { id: 'parking_availability', label: 'Please describe parking availability for cleaning team', type: 'select', options: [
    { id: 'free_onsite', label: 'Free parking on-site' },
    { id: 'limited', label: 'Limited parking' },
    { id: 'street_only', label: 'Street parking only' },
    { id: 'none', label: 'None available' }
  ], required: true },
  
  { id: 'utilities_available', label: 'Please select all that will be available', type: 'multi-select', options: [
    { id: 'hot_water', label: 'Hot water' },
    { id: 'cold_water', label: 'Cold water' },
    { id: 'power', label: 'Power outlets' },
    { id: 'waste_disposal', label: 'Waste disposal' }
  ], required: false },
  
  { id: 'num_rooms', label: 'Please confirm the number of rooms at the venue', type: 'select', options: [
    { id: '1-5', label: '1-5 rooms' },
    { id: '6-10', label: '6-10 rooms' },
    { id: '11-20', label: '11-20 rooms' },
    { id: '20+', label: '20+ rooms' }
  ], required: true },
  
  { id: 'waste_location', label: 'Which best describes the location of the waste collection point', type: 'select', options: [
    { id: 'onsite_accessible', label: 'On-site and accessible' },
    { id: 'onsite_limited', label: 'On-site but limited access' },
    { id: 'offsite', label: 'Off-site' }
  ], required: true }
];

// HMO SERVICE TYPES
const HMO_SERVICE_TYPES = [
  { id: 1, label: 'Core Communal Cleaning', icon: '🧹', description: 'Regular communal area cleaning' },
  { id: 2, label: 'Room/Void Turnover Cleaning', icon: '🔄', description: 'Between-tenant room cleaning' },
  { id: 3, label: 'Planned Periodic Resets', icon: '📅', description: 'Scheduled deep cleaning cycles' }
];

// HMO QUESTIONS
const HMO_QUESTIONS = [
  { id: 'kitchens', label: 'Please Confirm The Number of Kitchens and Kitchenettes', type: 'select', options: [
    { id: '0', label: '0' },
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4+', label: '4+' }
  ], required: true },
  
  { id: 'bathrooms', label: 'Please Confirm The Number of Bathrooms & Toilets', type: 'select', options: [
    { id: '0', label: '0' },
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4+', label: '4+' }
  ], required: true },
  
  { id: 'floor_area', label: 'Which best describes the floor area in Square Metres?', type: 'select', options: [
    { id: '60', label: 'Under 80 m²' },
    { id: '110', label: '80-140 m²' },
    { id: '180', label: '140-220 m²' },
    { id: '285', label: '220-350 m²' },
    { id: '425', label: '350-500 m²' },
    { id: '600', label: '500+ m²' }
  ], required: true },
  
  { id: 'occupants', label: 'How Many Occupants?', type: 'select', options: [
    { id: '1-3', label: '1-3' },
    { id: '4-6', label: '4-6' },
    { id: '7-9', label: '7-9' },
    { id: '10-12', label: '10-12' },
    { id: '13-20', label: '13-20' },
    { id: '21+', label: '21+' }
  ], required: true },
  
  { id: 'floor_levels', label: 'Please confirm the number of relevant floor levels', type: 'select', options: [
    { id: '0-1', label: '0-1 floors' },
    { id: '2-3', label: '2-3 floors' },
    { id: '4-6', label: '4-6 floors' }
  ], required: true },
  
  { id: 'turnaround', label: 'Please Confirm The Turnaround Window', type: 'select', options: [
    { id: 'next_day', label: 'Next day' },
    { id: '48h', label: '48 hours' },
    { id: '72h_plus', label: '72+ hours' }
  ], required: true }
];

// SPECIALIST CLEANING SERVICE TYPES
const SPECIALIST_CLEANING_SERVICE_TYPES = [
  { id: 1, label: 'Moulding & Plastering Cleanup', icon: '🏗️', description: 'Specialist dust and debris cleaning' },
  { id: 2, label: 'Mould Removal & Treatment', icon: '🍄', description: 'Mould remediation and deep cleaning' },
  { id: 3, label: 'Fire & Smoke Damage Cleanup', icon: '🔥', description: 'Post-fire cleaning and restoration' },
  { id: 4, label: 'Flood & Water Damage Cleanup', icon: '💧', description: 'Post-flood drying and sanitization' }
];

// SPECIALIST CLEANING QUESTIONS
const SPECIALIST_CLEANING_QUESTIONS = [
  { id: 'residue_types', label: 'Which of the following Residue Types are to be included in this service?', type: 'multi-select', options: [
    { id: 'heavy_silica_dust', label: 'Heavy Silica Dust' },
    { id: 'gypsum_dust', label: 'Gypsum Dust' },
    { id: 'gypsum_buildup', label: 'Gypsum Buildup' },
    { id: 'paint_splatter', label: 'Paint Splatter' },
    { id: 'silicone_smears', label: 'Silicone Smears' },
    { id: 'grout_haze', label: 'Grout Haze' },
    { id: 'adhesives_glues', label: 'Adhesives/Glues' },
    { id: 'protective_films', label: 'Protective Films' },
    { id: 'other', label: 'Other' }
  ], required: true },
  
  { id: 'build_phase', label: 'Considered by the Build Phase, which is most relevant?', type: 'select', options: [
    { id: 'debris_clean', label: 'Debris Clean' },
    { id: 'intensive_scrub', label: 'Intensive Scrub' },
    { id: 'polish', label: 'Polish' }
  ], required: true },
  
  { id: 'site_activity', label: 'Which of the following best describes Site Activity?', type: 'select', options: [
    { id: 'still_active', label: 'Still Active' },
    { id: 'ready_handover', label: 'Ready For Handover' },
    { id: 'other', label: 'Other' }
  ], required: true },
  
  { id: 'floor_levels', label: 'Please confirm the number of relevant floor levels.', type: 'select', options: [
    { id: '1', label: '1 level' },
    { id: '2', label: '2 levels' },
    { id: '3', label: '3 levels' },
    { id: '4', label: '4 levels' },
    { id: '5+', label: '5+ levels' }
  ], required: true }
];

// RESIDENTIAL SERVICE TYPES
const RESIDENTIAL_SERVICE_TYPES_FALLBACK = [
  { id: 'after-builders', label: 'After-Builders Cleaning (Residential scale)', description: 'Post-construction cleanup', property_id: 'residential' },
  { id: 'end-of-tenancy', label: 'End of Tenancy Cleaning (EoT)', description: 'End of tenancy cleaning', property_id: 'residential' },
  { id: 'deep-cleaning', label: 'Deep Cleaning', description: 'Thorough deep clean', property_id: 'residential' },
  { id: 'regular-domestic', label: 'Regular Domestic Cleaning', description: 'Regular home cleaning', property_id: 'residential' },
  { id: 'specialist', label: 'Specialist Cleaning', description: 'Mould/Fire/Flood cleanup and restoration', property_id: 'specialist' }
];

// RESIDENTIAL QUESTIONS
const RESIDENTIAL_QUESTIONS = [
  { id: 'residue_types', label: 'Which of the following Residue Types are to be included in this service?', type: 'multi-select', conditional: 'service_type:after-builders', options: [
    { id: 'heavy_silica_dust', label: 'Heavy Silica Dust' },
    { id: 'gypsum_dust', label: 'Gypsum Dust' },
    { id: 'gypsum_buildup', label: 'Gypsum Buildup' },
    { id: 'paint_splatter', label: 'Paint Splatter' },
    { id: 'silicone_smears', label: 'Silicone Smears' },
    { id: 'grout_haze', label: 'Grout Haze' },
    { id: 'adhesives_glues', label: 'Adhesives/Glues' },
    { id: 'protective_films', label: 'Protective Films' },
    { id: 'other', label: 'Other' }
  ], required: false },
  
  { id: 'build_phase', label: 'Considered by the Build Phase, which is most relevant?', type: 'select', conditional: 'service_type:after-builders', options: [
    { id: 'debris_clean', label: 'Debris Clean' },
    { id: 'intensive_scrub', label: 'Intensive Scrub' },
    { id: 'polish', label: 'Polish' }
  ], required: false },
  
  { id: 'site_activity', label: 'Which of the following best describes Site Activity?', type: 'select', conditional: 'service_type:after-builders', options: [
    { id: 'still_active', label: 'Still Active' },
    { id: 'ready_handover', label: 'Ready For Handover' },
    { id: 'other', label: 'Other' }
  ], required: false },
  
  { id: 'bedrooms', label: 'Number of bedrooms', type: 'select', options: [
    { value: 1, label: '1 bedroom' },
    { value: 2, label: '2 bedrooms' },
    { value: 3, label: '3 bedrooms' },
    { value: 4, label: '4 bedrooms' },
    { value: 5, label: '5 bedrooms' },
    { value: 6, label: '6+ bedrooms' }
  ], required: true },
  
  { id: 'floor_area', label: 'Approximate floor area (sq m)', type: 'select', options: [
    { value: '40', label: 'Up to 40 sq m (studio/1-bed flat)' },
    { value: '70', label: '40-70 sq m (1-2 bed flat)' },
    { value: '100', label: '70-100 sq m (2-3 bed semi)' },
    { value: '140', label: '100-140 sq m (3-4 bed detached)' },
    { value: '200', label: '140-200 sq m (4-5 bed house)' },
    { value: '300', label: '200-300 sq m (5-6 bed large house)' },
    { value: '400', label: 'Over 300 sq m (mansion/large property)' }
  ], required: true },
  
  { id: 'receptions', label: 'Number of reception rooms', type: 'select', options: [
    { value: 0, label: 'None' },
    { value: 1, label: '1 reception room' },
    { value: 2, label: '2 reception rooms' },
    { value: 3, label: '3+ reception rooms' }
  ], required: true },
  
  { id: 'furnishing_level', label: 'Furnishing level', type: 'select', options: [
    { id: 'unfurnished', label: 'Unfurnished' },
    { id: 'part_furnished', label: 'Part Furnished' },
    { id: 'furnished', label: 'Furnished' },
    { id: 'luxury_furnished', label: 'Luxury Furnished' }
  ], required: true },
  
  { id: 'floor_levels', label: 'Please confirm the number of relevant floor levels.', type: 'select', options: [
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4', label: '4' },
    { id: '5', label: '5' },
    { id: '6+', label: '6 or more' }
  ], required: true },
  
  { id: 'bathrooms_toilets', label: 'Please confirm the number of bathrooms and toilets.', type: 'select', options: [
    { id: '0', label: '0' },
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4', label: '4' },
    { id: '5', label: '5' },
    { id: '6+', label: '6 or more' }
  ], required: true },
  
  { id: 'kitchens', label: 'Number of kitchens', type: 'select', options: [
    { id: '0', label: '0 kitchens' },
    { id: '1', label: '1 kitchen (1.0 hours)' },
    { id: '2', label: '2 kitchens (2.0 hours)' },
    { id: '3', label: '3 kitchens (3.0 hours)' },
    { id: '4', label: '4+ kitchens (3.0 hours)' }
  ], required: true },
  
  { id: 'additional_areas', label: 'Additional areas to clean (select all that apply)', type: 'multi-select', options: [
    { id: 'garage', label: 'Garage (1.0 hours)' },
    { id: 'utility_room', label: 'Utility Room (0.75 hours)' },
    { id: 'conservatory', label: 'Conservatory (1.25 hours)' },
    { id: 'hallway_stairs', label: 'Hallway & Stairs (1.5 hours)' },
    { id: 'laundry_room', label: 'Laundry Room (0.75 hours)' },
    { id: 'guest_toilet', label: 'Guest Toilet (0.5 hours)' },
    { id: 'storage_cupboards', label: 'Storage Cupboards (0.75 hours)' }
  ], required: false },
  
  { id: 'residue_type_selection', label: 'Residue Types in property (multi-select)', type: 'multi-select', options: [
    { id: 'heavy_silica_dust', label: 'Heavy Silica Dust' },
    { id: 'gypsum_dust', label: 'Gypsum Dust' },
    { id: 'gypsum_buildup', label: 'Gypsum Buildup' },
    { id: 'paint_splatter', label: 'Paint Splatter' },
    { id: 'silicone_smears', label: 'Silicone Smears' },
    { id: 'grout_haze', label: 'Grout Haze' },
    { id: 'adhesives_glues', label: 'Adhesives/Glues' },
    { id: 'protective_films', label: 'Protective Films' },
    { id: 'other', label: 'Other' }
  ], required: false },
  
  { id: 'furnishing_level_multiplier', label: 'Furnishing level (multiplier)', type: 'select', options: [
    { id: 'unfurnished', label: 'Unfurnished (×0.92)' },
    { id: 'part_furnished', label: 'Part Furnished (×1.00)' },
    { id: 'furnished', label: 'Furnished (×1.10)' },
    { id: 'luxury_furnished', label: 'Luxury Furnished (×1.20)' }
  ], required: false },
  
  { id: 'parking_availability', label: 'Parking availability for team', type: 'select', options: [
    { id: 'free', label: 'Free (×1.00)' },
    { id: 'paid', label: 'Paid (+£8)' },
    { id: 'limited', label: 'Limited (×1.06)' },
    { id: 'none', label: 'None (×1.10 + £15)' }
  ], required: false },
  
  { id: 'urgency_level', label: 'Service urgency / timeline', type: 'select', options: [
    { id: 'same_day', label: 'Same Day (×1.30)' },
    { id: 'next_day', label: 'Next Day (×1.20)' },
    { id: '2days', label: '2 Days (×1.12)' },
    { id: '3_5days', label: '3-5 Days (×1.05)' },
    { id: '1week', label: '1 Week (×1.00)' },
    { id: 'flexible', label: 'Flexible (×0.97)' }
  ], required: false },
  
  { id: 'access_hours', label: 'Access window for cleaning', type: 'select', options: [
    { id: 'standard_daytime', label: 'Standard Daytime (×1.00)' },
    { id: 'morning_only', label: 'Morning Only (×1.00)' },
    { id: 'afternoon_only', label: 'Afternoon Only (×1.00)' },
    { id: 'evening_only', label: 'Evening Only (×1.10)' },
    { id: 'weekend', label: 'Weekend (×1.12)' },
    { id: 'tight_window', label: 'Tight Time Window (×1.15)' }
  ], required: false },
  
  { id: 'build_phase', label: 'Build phase (After-Builders)', type: 'select', conditional: 'service_type:after-builders', options: [
    { id: 'debris_clean', label: 'Debris Clean' },
    { id: 'intensive_scrub', label: 'Intensive Scrub' },
    { id: 'polish', label: 'Polish' }
  ], required: false },
  
  { id: 'site_activity', label: 'Site activity status (After-Builders)', type: 'select', conditional: 'service_type:after-builders', options: [
    { id: 'still_active', label: 'Still Active' },
    { id: 'ready_for_handover', label: 'Ready For Handover' }
  ], required: false },
  
  { id: 'postcode_uplift', label: 'Postcode location uplift (ADDITIVE)', type: 'select', options: [
    { id: 'none', label: 'No uplift (+£0)' },
    { id: 'standard', label: 'Standard uplift (+£10)' },
    { id: 'premium', label: 'Premium uplift (+£20)' },
    { id: 'maximum', label: 'Maximum uplift (+£25)' }
  ], required: false },
  
  { id: 'business_rules_check', label: 'Business Rule Conditions (check all that apply)', type: 'multi-select', options: [
    { id: 'residue_3plus', label: 'Residue count ≥ 3 types → Range Quote −10% confidence' },
    { id: 'site_active', label: 'Site Still Active (After-Builders) → −15% confidence' },
    { id: 'emergency_service', label: 'Emergency service (same/next day) → −20% confidence' },
    { id: 'not_sure', label: 'Not sure about property details → −25% confidence (base 75%)' }
  ], required: false },
  
  { id: 'confidence_calculation', label: 'Confidence Score Calculation', type: 'info', message: 'Starting Score: 100%\n\nAutomatically Reduced By:\n• Residue ≥3 types: −10%\n• Site still active: −15%\n• Emergency service: −20%\n• Not sure details: −25%\n\nPricing Ranges:\n• <50%: ±25%\n• 50-69%: ±20%\n• 70-84%: ±15%\n• 85%+: ±10%' }
];

// CARPENTRY & JOINERY SERVICE TYPES
const CARPENTRY_SERVICE_TYPES = [
  { id: 9, label: 'Bespoke Joinery', icon: '🪵', description: 'Custom built-in joinery and storage solutions', property_id: 'carpentry' },
  { id: 10, label: 'Cabinetry & Storage', icon: '🚪', description: 'Custom cabinetry and storage systems', property_id: 'carpentry' },
  { id: 11, label: 'Carpentry (1st Fix)', icon: '🔨', description: '1st fix structural carpentry', property_id: 'carpentry' },
  { id: 12, label: 'Carpentry (2nd Fix)', icon: '🪛', description: '2nd fix doors, skirting and architraves', property_id: 'carpentry' },
  { id: 13, label: 'Door Installation', icon: '🚪', description: 'Door hanging and installation', property_id: 'carpentry' },
  { id: 14, label: 'Flooring', icon: '⬜', description: 'Wood and laminate flooring', property_id: 'carpentry' },
  { id: 15, label: 'Hoarding Systems', icon: '🛡️', description: 'Protective hoarding installation', property_id: 'carpentry' },
  { id: 16, label: 'Roofing Carpentry', icon: '🏠', description: 'Roof structures and fittings', property_id: 'carpentry' },
  { id: 17, label: 'Skirting & Architraves', icon: '📐', description: 'Skirting boards and architrave installation', property_id: 'carpentry' },
  { id: 18, label: 'Staircases', icon: '🪜', description: 'Staircase design and installation', property_id: 'carpentry' }
];

// CARPENTRY QUESTIONS
const CARPENTRY_QUESTIONS = [
  { id: 'project_scope', label: 'Please describe your project scope and requirements', type: 'textarea', required: true },
  { id: 'project_location', label: 'Where is the project located?', type: 'select', options: [
    { id: 'single_room', label: 'Single room' },
    { id: 'multi_room', label: 'Multiple rooms' },
    { id: 'full_property', label: 'Full property' }
  ], required: true },
  { id: 'room_type', label: 'Primary room type for work', type: 'select', options: [
    { id: 'bedroom', label: 'Bedroom' },
    { id: 'kitchen', label: 'Kitchen' },
    { id: 'bathroom', label: 'Bathroom' },
    { id: 'living_room', label: 'Living Room' },
    { id: 'other', label: 'Other' }
  ], required: true },
  { id: 'access', label: 'Property access and occupancy status', type: 'select', options: [
    { id: 'full_access_vacant', label: 'Full access - property vacant' },
    { id: 'full_access_occupied', label: 'Full access - property occupied' },
    { id: 'partial_access', label: 'Partial access - restricted hours' },
    { id: 'limited_access', label: 'Limited access' }
  ], required: true },
  { id: 'existing_condition', label: 'Condition of existing surfaces/structure', type: 'select', options: [
    { id: 'new_build', label: 'New build - clean slate' },
    { id: 'good_condition', label: 'Good condition - minor work' },
    { id: 'fair_condition', label: 'Fair condition - moderate adjustments' },
    { id: 'poor_condition', label: 'Poor condition - significant issues' }
  ], required: true },
  { id: 'timeline', label: 'When do you need the work completed?', type: 'select', options: [
    { id: 'urgent', label: 'Urgent (within 1 week)' },
    { id: 'soon', label: 'Soon (within 2-3 weeks)' },
    { id: '1month', label: 'Within 1 month' },
    { id: 'flexible', label: 'Flexible - no deadline' }
  ], required: true },
  { id: 'design_ready', label: 'Do you have design/drawings ready?', type: 'select', options: [
    { id: 'full_drawings', label: 'Yes - full technical drawings' },
    { id: 'concepts', label: 'Yes - concept/inspiration sketches' },
    { id: 'example', label: 'Yes - example/similar project to replicate' },
    { id: 'no_design', label: 'No - need design consultation' }
  ], required: false },
  { id: 'material_preference', label: 'Material preference (if applicable)', type: 'select', options: [
    { id: 'mdf_laminate', label: 'MDF/Laminate (budget-friendly)' },
    { id: 'plywood', label: 'Plywood (mid-range)' },
    { id: 'hardwood', label: 'Hardwood (premium)' },
    { id: 'mixed', label: 'Mixed materials' },
    { id: 'client_decides', label: 'To be decided' }
  ], required: false },
  { id: 'finish_type', label: 'Finish type preference', type: 'select', options: [
    { id: 'paint', label: 'Paint' },
    { id: 'stain_varnish', label: 'Stain/Varnish' },
    { id: 'natural', label: 'Natural finish' },
    { id: 'other', label: 'Other/TBD' }
  ], required: false },
  { id: 'parking', label: 'Parking availability for tradespeople', type: 'select', options: [
    { id: 'free_onsite', label: 'Free on-site parking' },
    { id: 'limited', label: 'Limited parking' },
    { id: 'street', label: 'Street parking only' },
    { id: 'none', label: 'No parking available' }
  ], required: true },
  { id: 'budget_range', label: 'Do you have a budget range in mind?', type: 'select', options: [
    { id: 'under_500', label: 'Under £500' },
    { id: '500_1000', label: '£500-£1,000' },
    { id: '1000_2500', label: '£1,000-£2,500' },
    { id: '2500_5000', label: '£2,500-£5,000' },
    { id: 'over_5000', label: 'Over £5,000' },
    { id: 'not_sure', label: 'Not sure' }
  ], required: false }
];

// BESPOKE JOINERY QUESTIONS (Service Type ID 9)
const BESPOKE_JOINERY_QUESTIONS = [
  { id: 'bespoke_joinery_type', label: 'Q1. What bespoke joinery is required?', type: 'select', options: [
    { id: 'fitted_wardrobes', label: 'Fitted wardrobes' },
    { id: 'alcove_shelving', label: 'Alcove units / shelving' },
    { id: 'media_wall', label: 'Media wall / TV unit' },
    { id: 'built_in_cupboards', label: 'Built-in cupboards / storage' },
    { id: 'window_seats', label: 'Window seats / bench seating' },
    { id: 'wall_panelling', label: 'Wall panelling / feature joinery' },
    { id: 'home_office', label: 'Home office / desk units' },
    { id: 'stair_components', label: 'Stair components (handrails, balustrades)' },
    { id: 'mixed_joinery', label: 'Mixed / whole-room joinery' },
    { id: 'unsure', label: 'Unsure' }
  ], required: true },
  { id: 'design_information', label: 'Q2. What design information exists?', type: 'select', options: [
    { id: 'detailed_drawings', label: 'Fully detailed drawings (joinery-ready)' },
    { id: 'concept_sketches', label: 'Concept sketches / inspiration images' },
    { id: 'similar_example', label: 'Similar example to replicate' },
    { id: 'no_design', label: 'No design yet' },
    { id: 'design_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'installation_location', label: 'Q3. Where will the joinery be installed?', type: 'select', options: [
    { id: 'bedroom', label: 'Bedroom' },
    { id: 'living_room', label: 'Living room' },
    { id: 'kitchen_utility', label: 'Kitchen / utility' },
    { id: 'hallway_landing', label: 'Hallway / landing' },
    { id: 'home_office_room', label: 'Home office' },
    { id: 'multiple_rooms', label: 'Multiple rooms' },
    { id: 'location_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'joinery_size', label: 'Q4. Approximate size of joinery?', type: 'select', options: [
    { id: 'small', label: 'Small (single alcove / cupboard)' },
    { id: 'medium', label: 'Medium (wardrobe / wall unit)' },
    { id: 'large', label: 'Large (full wall / multiple units)' },
    { id: 'whole_room', label: 'Whole room / wrap-around' },
    { id: 'size_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'primary_material', label: 'Q5. Primary material?', type: 'select', options: [
    { id: 'mdf_painted', label: 'MDF (painted)' },
    { id: 'veneered_mdf', label: 'Veneered MDF' },
    { id: 'plywood', label: 'Plywood' },
    { id: 'hardwood', label: 'Hardwood (oak, ash, walnut)' },
    { id: 'mixed_materials', label: 'Mixed materials' },
    { id: 'material_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'finish_required', label: 'Q6. Finish required?', type: 'select', options: [
    { id: 'paint_hand_finished', label: 'Paint (hand finished)' },
    { id: 'spray_finish', label: 'Spray finish (factory)' },
    { id: 'stain_oil', label: 'Stain / oil' },
    { id: 'natural_unfinished', label: 'Natural / unfinished' },
    { id: 'finish_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'internal_features', label: 'Q7. Internal features required? (select all that apply)', type: 'multiselect', options: [
    { id: 'shelves', label: 'Shelves' },
    { id: 'drawers', label: 'Drawers' },
    { id: 'hanging_rails', label: 'Hanging rails' },
    { id: 'soft_close', label: 'Soft-close mechanisms' },
    { id: 'internal_lighting', label: 'Internal lighting' },
    { id: 'cable_management', label: 'Cable management' },
    { id: 'no_features', label: 'None' }
  ], required: false },
  { id: 'wall_floor_condition', label: 'Q8. Wall / floor condition?', type: 'select', options: [
    { id: 'straight_level', label: 'Straight & level' },
    { id: 'minor_unevenness', label: 'Minor unevenness' },
    { id: 'period_uneven', label: 'Period property / uneven' },
    { id: 'condition_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'access_installation', label: 'Q9. Access for installation?', type: 'select', options: [
    { id: 'easy_access', label: 'Easy access / clear rooms' },
    { id: 'partially_occupied', label: 'Partially occupied' },
    { id: 'fully_occupied', label: 'Fully occupied' },
    { id: 'restricted_access', label: 'Restricted access / tight staircases' },
    { id: 'access_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'measurements_responsibility', label: 'Q10. Who is responsible for final measurements?', type: 'select', options: [
    { id: 'joiner_measures', label: 'Joiner to measure on site' },
    { id: 'client_provides', label: 'Client provides dimensions' },
    { id: 'architect_drawings', label: 'Architect drawings only' },
    { id: 'measurements_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'installation_timing', label: 'Q11. When should installation occur?', type: 'select', options: [
    { id: 'ongoing_renovation', label: 'Part of ongoing renovation' },
    { id: 'standalone', label: 'Standalone installation' },
    { id: 'time_critical', label: 'Time-critical (fixed date)' },
    { id: 'timing_unsure', label: 'Unsure' }
  ], required: true },
  { id: 'quality_level', label: 'Q12. Expected quality level?', type: 'select', options: [
    { id: 'functional', label: 'Functional bespoke' },
    { id: 'mid_range', label: 'Mid-range bespoke' },
    { id: 'high_end', label: 'High-end / furniture grade' },
    { id: 'quality_unsure', label: 'Unsure' }
  ], required: true }
];

// CORE CONSTRUCTION & STRUCTURAL SERVICE TYPES
const CORE_CONSTRUCTION_SERVICE_TYPES = [
  { id: 126, label: 'Blockwork', icon: '🧱', description: 'Concrete block construction', property_id: 'blockwork' },
  { id: 127, label: 'Bricklaying', icon: '🧱', description: 'Brick and stone masonry', property_id: 'bricklaying' },
  { id: 128, label: 'Concrete Works', icon: '🔨', description: 'Concrete casting and finishing', property_id: 'concrete_works' },
  { id: 129, label: 'Foundations & Footings', icon: '🏗️', description: 'Foundation design and construction', property_id: 'foundations_footings' },
  { id: 130, label: 'Groundworks', icon: '⛏️', description: 'Site excavation and preparation', property_id: 'groundworks' },
  { id: 131, label: 'Steel Installation', icon: '🏭', description: 'RSJs, beams, and structural steel', property_id: 'steel_installation' },
  { id: 132, label: 'Structural Repairs', icon: '🔧', description: 'Structural damage repair', property_id: 'structural_repairs' },
  { id: 133, label: 'Underpinning', icon: '🏢', description: 'Foundation underpinning', property_id: 'underpinning' }
];

// ELECTRICAL & SMART SYSTEMS SERVICE TYPES
const ELECTRICAL_SERVICE_TYPES = [
  { id: 'smart-home-installation', label: 'Smart Home Installation', icon: '🏠', description: 'Home automation and smart systems', property_id: 'smart_home_installation' },
  { id: 'rewiring-full-partial', label: 'Rewiring (Full/Partial)', icon: '⚡', description: 'Complete or partial electrical rewiring', property_id: 'rewiring_full_partial' },
  { id: 'pat-testing', label: 'PAT Testing', icon: '🔌', description: 'Portable Appliance Testing', property_id: 'pat_testing' },
  { id: 'lighting-installation', label: 'Lighting Installation', icon: '💡', description: 'Interior and exterior lighting', property_id: 'lighting_installation' },
  { id: 'intruder-alarms', label: 'Intruder & Burglar Alarms', icon: '🚨', description: 'Security alarm systems', property_id: 'intruder_alarms' },
  { id: 'fire-alarm-systems', label: 'Fire Alarm Systems', icon: '🔥', description: 'Fire detection and alarm systems', property_id: 'fire_alarm_systems' },
  { id: 'ev-charger-installation', label: 'EV Charger Installation', icon: '🔋', description: 'Electric vehicle charging points', property_id: 'ev_charger_installation' },
  { id: 'emergency-lighting', label: 'Emergency Lighting', icon: '⚠️', description: 'Emergency exit and safety lighting', property_id: 'emergency_lighting' },
];

// LANDSCAPING & EXTERNAL WORKS - Services 170-178
const LANDSCAPING_SERVICE_TYPES = [
  { id: 170, label: 'Decking', icon: '🪵', description: 'Wooden decking installation', property_id: 'decking' },
  { id: 171, label: 'Fencing', icon: '🚧', description: 'Fence installation and repair', property_id: 'fencing' },
  { id: 172, label: 'Garden Clearance', icon: '🧹', description: 'Clear overgrown gardens', property_id: 'garden_clearance' },
  { id: 173, label: 'Garden Maintenance', icon: '🌱', description: 'Regular garden maintenance', property_id: 'garden_maintenance' },
  { id: 174, label: 'Hedge Cutting', icon: '✂️', description: 'Hedge trimming and cutting', property_id: 'hedge_cutting' },
  { id: 175, label: 'Landscaping', icon: '🏡', description: 'Full landscaping design and build', property_id: 'landscaping' },
  { id: 176, label: 'Pressure Washing', icon: '💧', description: 'Pressure cleaning services', property_id: 'pressure_washing' },
  { id: 177, label: 'Tree Surgery', icon: '🌳', description: 'Tree trimming and removal', property_id: 'tree_surgery' },
  { id: 178, label: 'Turfing', icon: '🌿', description: 'Lawn installation and seeding', property_id: 'turfing' },
];

// FINISHING & INTERIORS SERVICES - Services 200-208
const FINISHING_INTERIORS_SERVICE_TYPES = [
  { id: 200, label: 'Ceiling Installation', icon: '⬆️', description: 'Plasterboard and suspended ceilings', property_id: 'ceiling_installation' },
  { id: 201, label: 'Drylining', icon: '📐', description: 'Wall and partition drylining', property_id: 'drylining' },
  { id: 202, label: 'Feature Walls', icon: '✨', description: 'Decorative wall features', property_id: 'feature_walls' },
  { id: 203, label: 'Flooring', icon: '🪵', description: 'Carpet, vinyl, and LVT installation', property_id: 'flooring' },
  { id: 204, label: 'Painting & Decorating', icon: '🎨', description: 'Interior painting and decoration', property_id: 'painting_decorating' },
  { id: 205, label: 'Plastering', icon: '🧱', description: 'Plastering and skimming', property_id: 'plastering' },
  { id: 206, label: 'Rendering', icon: '🏗️', description: 'External rendering systems', property_id: 'rendering' },
  { id: 207, label: 'Tiling', icon: '🧩', description: 'Wall and floor tiling', property_id: 'tiling' },
  { id: 208, label: 'Wallpapering', icon: '🧻', description: 'Wallpaper hanging and removal', property_id: 'wallpapering' },
];

// KITCHENS & BATHROOMS SERVICES - Services 300-306
const KITCHENS_BATHROOMS_SERVICE_TYPES = [
  { id: 300, label: 'Bathroom Installation', icon: '🚿', description: 'Full bathroom installation', property_id: 'bathroom_installation' },
  { id: 301, label: 'Bathroom Refurbishment', icon: '🛁', description: 'Bathroom refurbishment and upgrades', property_id: 'bathroom_refurbishment' },
  { id: 302, label: 'Full Kitchen Installation', icon: '🍽️', description: 'Complete kitchen installation', property_id: 'kitchen_installation' },
  { id: 303, label: 'Kitchen Refurbishment', icon: '🔧', description: 'Kitchen upgrades and modifications', property_id: 'kitchen_refurbishment' },
  { id: 304, label: 'Vanity & Sanitaryware', icon: '🚽', description: 'Basin, WC, and vanity unit installation', property_id: 'vanity_sanitaryware' },
  { id: 305, label: 'Washrooms', icon: '🔌', description: 'Compact washroom installation', property_id: 'washrooms' },
  { id: 306, label: 'Wet Rooms', icon: '💧', description: 'Wet room design and installation', property_id: 'wet_rooms' },
];

// LOCKS & SECURITY SERVICES - Services 400-401
const LOCKS_SECURITY_SERVICE_TYPES = [
  { id: 400, label: 'Lock Changes', icon: '🔐', description: 'Lock replacement and installation', property_id: 'lock_changes' },
  { id: 401, label: 'Locksmith Services', icon: '🔑', description: 'Professional locksmith services', property_id: 'locksmith_services' },
];

// PLUMBING, HEATING & GAS SERVICES - Services 500-512
const PLUMBING_SERVICE_TYPES = [
  { id: 500, label: 'Bathroom Plumbing', icon: '🚿', description: 'Bathroom plumbing installation', property_id: 'bathroom_plumbing' },
  { id: 501, label: 'Boiler Installation', icon: '🔥', description: 'Boiler installation and replacement', property_id: 'boiler_installation' },
  { id: 502, label: 'Central Heating Installation', icon: '🌡️', description: 'Central heating system installation', property_id: 'central_heating' },
  { id: 503, label: 'Emergency Plumbing', icon: '🚨', description: 'Emergency plumbing repairs', property_id: 'emergency_plumbing' },
  { id: 504, label: 'Gas Engineering', icon: '⚙️', description: 'Gas installation and servicing', property_id: 'gas_engineering' },
  { id: 505, label: 'General Plumbing', icon: '🔧', description: 'General plumbing services', property_id: 'general_plumbing' },
  { id: 506, label: 'Hot Water Cylinders', icon: '💧', description: 'Hot water cylinder installation', property_id: 'hot_water_cylinders' },
  { id: 507, label: 'Kitchen Plumbing', icon: '🍽️', description: 'Kitchen plumbing installation', property_id: 'kitchen_plumbing' },
  { id: 508, label: 'Leak Detection & Repair', icon: '💧', description: 'Leak detection and repair', property_id: 'leak_detection' },
  { id: 509, label: 'Pipework Installation', icon: '🔌', description: 'Pipework installation and replacement', property_id: 'pipework_installation' },
  { id: 510, label: 'Radiator Installation', icon: '🌡️', description: 'Radiator installation and balancing', property_id: 'radiator_installation' },
  { id: 511, label: 'Underfloor Heating', icon: '🔥', description: 'Underfloor heating installation', property_id: 'underfloor_heating' },
  { id: 512, label: 'Unvented Systems', icon: '💨', description: 'Unvented heating system installation', property_id: 'unvented_systems' },
];

// PROPERTY MAINTENANCE & HANDYMAN SERVICES - Services 600-604
const PROPERTY_MAINTENANCE_SERVICE_TYPES = [
  { id: 600, label: 'Furniture Assembly', icon: '🪑', description: 'Furniture assembly and installation', property_id: 'furniture_assembly' },
  { id: 601, label: 'General Property Maintenance', icon: '🏠', description: 'General property maintenance', property_id: 'general_maintenance' },
  { id: 602, label: 'Planned Preventative Maintenance', icon: '📋', description: 'PPM and scheduled maintenance', property_id: 'planned_maintenance' },
  { id: 603, label: 'Reactive Maintenance', icon: '⚡', description: 'Emergency reactive maintenance', property_id: 'reactive_maintenance' },
  { id: 604, label: 'Wall Mounting', icon: '📺', description: 'TV and fixture wall mounting', property_id: 'wall_mounting' },
];

// ROOFING SERVICES - Services 700-705
const ROOFING_SERVICE_TYPES = [
  { id: 700, label: 'Chimney Repairs', icon: '🔱', description: 'Chimney repair and maintenance', property_id: 'chimney_repairs' },
  { id: 701, label: 'Fascias, Soffits & Guttering', icon: '🏠', description: 'Fascia, soffit, and gutter installation', property_id: 'fascias_guttering' },
  { id: 702, label: 'Flat Roofing', icon: '🏗️', description: 'Flat roof installation and repair', property_id: 'flat_roofing' },
  { id: 703, label: 'Leadwork', icon: '⛓️', description: 'Lead roof installation and repair', property_id: 'leadwork' },
  { id: 704, label: 'Pitched Roofing', icon: '🏠', description: 'Pitched roof installation and repair', property_id: 'pitched_roofing' },
  { id: 705, label: 'Roof Inspections', icon: '🔍', description: 'Professional roof inspections', property_id: 'roof_inspections' },
];

// CORE CONSTRUCTION - SEMANTIC FIELD NAME MAPPINGS
// Maps question codes (Q1, Q2, etc.) to semantic field names for each subcategory
const CORE_CONSTRUCTION_FIELD_MAPPINGS: Record<string, Record<string, string>> = {
  blockwork: {
    'Q1': 'blockwork_type',
    'Q2': 'blockwork_location',
    'Q3': 'blockwork_supports',
    'Q4': 'blockwork_size',
    'Q5': 'blockwork_height_storeys',
    'Q6': 'blockwork_block_type',
    'Q7': 'blockwork_materials_supplier',
    'Q8': 'blockwork_finish_level',
    'Q9': 'blockwork_demolition_required',
    'Q10': 'blockwork_new_footings_required',
    'Q11': 'blockwork_site_access',
    'Q12': 'blockwork_constraints',
    'Q13': 'blockwork_start_time'
  },
  bricklaying: {
    'Q1': 'bricklaying_type',
    'Q2': 'bricklaying_location',
    'Q3': 'bricklaying_load_bearing',
    'Q4': 'bricklaying_size',
    'Q5': 'bricklaying_height_storeys',
    'Q6': 'bricklaying_brick_type',
    'Q7': 'bricklaying_materials_supplier',
    'Q8': 'bricklaying_finish_level',
    'Q9': 'bricklaying_demolition_required',
    'Q10': 'bricklaying_new_footings_required',
    'Q11': 'bricklaying_site_access',
    'Q12': 'bricklaying_constraints',
    'Q13': 'bricklaying_start_time'
  },
  concrete_works: {
    'Q1': 'concrete_type',
    'Q2': 'concrete_structural',
    'Q3': 'concrete_size_volume',
    'Q4': 'concrete_thickness_depth',
    'Q5': 'concrete_reinforcement_required',
    'Q6': 'concrete_formwork_required',
    'Q7': 'concrete_placement_method',
    'Q8': 'concrete_finish_type',
    'Q9': 'concrete_ground_preparation',
    'Q10': 'concrete_curing_protection',
    'Q11': 'concrete_site_constraints',
    'Q12': 'concrete_building_control_approval',
    'Q13': 'concrete_start_timeframe'
  },
  foundations_footings: {
    'Q1': 'foundations_type',
    'Q2': 'foundations_supports',
    'Q3': 'foundations_depth_required',
    'Q4': 'foundations_size',
    'Q5': 'foundations_ground_conditions',
    'Q6': 'foundations_site_access',
    'Q7': 'foundations_excavated_spoil',
    'Q8': 'foundations_concrete_supply',
    'Q9': 'foundations_existing_services',
    'Q10': 'foundations_site_constraints',
    'Q11': 'foundations_building_control',
    'Q12': 'foundations_start_timeframe'
  },
  groundworks: {
    'Q1': 'groundworks_type',
    'Q2': 'groundworks_depth',
    'Q3': 'groundworks_size',
    'Q4': 'groundworks_machinery_access',
    'Q5': 'groundworks_excavated_material',
    'Q6': 'groundworks_ground_conditions',
    'Q7': 'groundworks_existing_services',
    'Q8': 'groundworks_site_constraints',
    'Q9': 'groundworks_building_control',
    'Q10': 'groundworks_start_timeframe'
  },
  steel_installation: {
    'Q1': 'steel_installation_type',
    'Q2': 'steel_installation_supports',
    'Q3': 'steel_installation_member_count',
    'Q4': 'steel_installation_size_weight',
    'Q5': 'steel_installation_load_bearing',
    'Q6': 'steel_installation_opening_width',
    'Q7': 'steel_installation_padstones',
    'Q8': 'steel_installation_temporary_support',
    'Q9': 'steel_installation_method',
    'Q10': 'steel_installation_property_height',
    'Q11': 'steel_installation_structural_engineer',
    'Q12': 'steel_installation_building_control',
    'Q13': 'steel_installation_property_occupied',
    'Q14': 'steel_installation_start_timeframe'
  },
  structural_repairs: {
    'Q1': 'structural_repairs_issue_type',
    'Q2': 'structural_repairs_severity',
    'Q3': 'structural_repairs_location',
    'Q4': 'structural_repairs_scale',
    'Q5': 'structural_repairs_method',
    'Q6': 'structural_repairs_temporary_support',
    'Q7': 'structural_repairs_access_conditions',
    'Q8': 'structural_repairs_structural_engineer',
    'Q9': 'structural_repairs_property_occupied',
    'Q10': 'structural_repairs_start_timeframe'
  },
  underpinning: {
    'Q1': 'underpinning_reason',
    'Q2': 'underpinning_method',
    'Q3': 'underpinning_property_affected',
    'Q4': 'underpinning_depth_required',
    'Q5': 'underpinning_ground_conditions',
    'Q6': 'underpinning_property_height',
    'Q7': 'underpinning_temporary_support',
    'Q8': 'underpinning_monitoring_required',
    'Q9': 'underpinning_site_access',
    'Q10': 'underpinning_structural_engineer_status',
    'Q11': 'underpinning_building_control_approval',
    'Q12': 'underpinning_start_timeframe'
  }
};

// CARPENTRY & JOINERY SERVICE TYPES
const CARPENTRY_SERVICE_TYPES_FALLBACK = [
  { id: 'bespoke-joinery', label: 'Bespoke Joinery', description: 'Custom built-in joinery and fitted storage', property_id: 'residential' },
  { id: 'cabinetry-storage', label: 'Cabinetry & Storage', description: 'Built-in cabinetry and storage solutions', property_id: 'residential' },
  { id: 'carpentry-1st-fix', label: 'Carpentry (1st Fix)', description: 'Structural carpentry: stud walls, floor joists, roof framing', property_id: 'residential' },
  { id: 'carpentry-2nd-fix', label: 'Carpentry (2nd Fix)', description: 'Doors, skirting, architraves, stair finishing', property_id: 'residential' },
  { id: 'door-installation', label: 'Door Installation', description: 'Internal and external door hanging and installation', property_id: 'residential' },
  { id: 'flooring-wood-laminate', label: 'Flooring (Wood & Laminate)', description: 'Wood and laminate flooring installation', property_id: 'residential' },
  { id: 'hoarding-systems', label: 'Hoarding Systems', description: 'Temporary and protective hoarding for renovation', property_id: 'residential' },
  { id: 'roofing-carpentry', label: 'Roofing Carpentry', description: 'Roof structure, trusses, and carpentry works', property_id: 'residential' },
  { id: 'skirting-architraves', label: 'Skirting & Architraves', description: 'Skirting board and architrave installation', property_id: 'residential' },
  { id: 'staircases', label: 'Staircases', description: 'Staircase supply, installation, and finishing', property_id: 'residential' }
];

// COMMERCIAL SERVICE TYPES
const COMMERCIAL_SERVICE_TYPES_FALLBACK = [
  { id: 'core-contract-cleaning', label: 'Core Contract Cleaning (Recurring)', description: 'Regular office maintenance' },
  { id: 'periodic-deep-cleans', label: 'Periodic & Planned Deep Cleans', description: 'Thorough commercial clean' },
  { id: 'commercial-carpet', label: 'Commercial Office Carpet Cleaning', description: 'Specialist carpet cleaning' }
];

// COMMERCIAL FLOOR AREA OPTIONS
const COMMERCIAL_FLOOR_AREA_OPTIONS = [
  { value: 75, label: 'Under 100 m²' },
  { value: 175, label: '100–250 m²' },
  { value: 375, label: '250–500 m²' },
  { value: 750, label: '500–1,000 m²' },
  { value: 1500, label: '1,000–2,000 m²' },
  { value: 3500, label: '2,000–5,000 m²' },
  { value: 6000, label: '5,000+ m² (Requires Quote)' },
  { value: 'not_sure', label: 'Not sure' }
];

// COMMERCIAL QUESTIONS
const COMMERCIAL_QUESTIONS = [
  { id: 'floor_area', label: 'Floor Area (Square Metres)', type: 'select', options: COMMERCIAL_FLOOR_AREA_OPTIONS, required: true },
  { id: 'floor_levels', label: 'Number of Floor Levels', type: 'select', options: [
    { id: '0-1', label: '0-1 floors' },
    { id: '2-3', label: '2-3 floors' },
    { id: '4-6', label: '4-6 floors' }
  ], required: true },
  { id: 'washrooms', label: 'Number of Washrooms', type: 'select', options: [
    { id: '0', label: '0' },
    { id: '1-2', label: '1-2' },
    { id: '3-4', label: '3-4' },
    { id: '5-6', label: '5-6' },
    { id: '7-10', label: '7-10' },
    { id: '11-15', label: '11-15' },
    { id: '16+', label: '16+' }
  ], required: true },
  { id: 'kitchens', label: 'Number of Kitchens', type: 'select', options: [
    { id: '0', label: '0' },
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4', label: '4' },
    { id: '5-6', label: '5-6' },
    { id: '7+', label: '7+' }
  ], required: true },
  { id: 'breakouts', label: 'Number of Breakouts/Messrooms', type: 'select', options: [
    { id: '0', label: '0' },
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4', label: '4' },
    { id: '5-6', label: '5-6' },
    { id: '7+', label: '7+' }
  ], required: true },
  { id: 'furniture_units', label: 'Number of Furniture Units (desks, tables, chairs)', type: 'select', options: [
    { id: '0-10', label: '0-10' },
    { id: '11-30', label: '11-30' },
    { id: '31-60', label: '31-60' },
    { id: '61-100', label: '61-100' },
    { id: '101-150', label: '101-150' },
    { id: '151-250', label: '151-250' },
    { id: '251+', label: '251+' }
  ], required: true },
  { id: 'urgency', label: 'Service Urgency / Notice Period', type: 'select', options: [
    { id: 'Emergency', label: 'Emergency (same day)' },
    { id: 'Urgent', label: 'Urgent (next day)' },
    { id: '1-2_weeks', label: 'Short notice (1-2 weeks)' },
    { id: '2-4_weeks', label: 'Standard (2-4 weeks)' },
    { id: 'Flexible', label: 'Flexible scheduling' }
  ], required: false },
  { id: 'parking', label: 'Parking Availability', type: 'select', options: [
    { id: 'free_available', label: 'Free parking available' },
    { id: 'limited_parking', label: 'Limited parking' },
    { id: 'no_parking', label: 'No parking available' }
  ], required: true },
  { id: 'frequency', label: 'How frequently do you require this service?', type: 'select', options: [
    { id: 'Daily', label: 'Daily' },
    { id: 'Weekly', label: 'Weekly' },
    { id: 'Monthly', label: 'Monthly' }
  ], required: false },
  { id: 'carpet_area_sqm', label: 'Carpet Area to Clean (Square Metres) - For Carpet Cleaning Only', type: 'number', required: false, placeholder: 'Leave blank if not doing carpet cleaning', conditional: 'service_type:commercial-carpet' },
  { id: 'carpet_condition', label: 'Carpet Condition - For Carpet Cleaning Only', type: 'select', conditional: 'service_type:commercial-carpet', options: [
    { id: 'normal', label: 'Normal condition' },
    { id: 'moderate_soiled', label: 'Moderately soiled' },
    { id: 'heavy_soiled', label: 'Heavily soiled' }
  ], required: false }
];

// Airbnb property details questions
const AIRBNB_DETAILS_QUESTIONS = [
  { id: 'floor_area', label: 'Floor Area (Square Metres)', type: 'select', options: FLOOR_AREA_OPTIONS, required: true },
  { id: 'floor_levels', label: 'Number of Floor Levels', type: 'select', options: FLOOR_LEVEL_OPTIONS, required: true },
  { id: 'bathrooms', label: 'Number of Bathrooms & Toilets', type: 'select', options: NUMBER_OPTIONS, required: true },
  { id: 'kitchens', label: 'Number of Kitchens', type: 'select', options: NUMBER_OPTIONS, required: true },
  { id: 'double_beds', label: 'Double/Queen/King Beds', type: 'select', options: NUMBER_OPTIONS, required: true },
  { id: 'single_beds', label: 'Single Beds', type: 'select', options: NUMBER_OPTIONS, required: true },
  { id: 'sofa_beds', label: 'Sofa Beds', type: 'select', options: NUMBER_OPTIONS, required: false },
  { id: 'receptions', label: 'Reception / Lounge / Living Rooms', type: 'select', options: NUMBER_OPTIONS, required: false },
  { id: 'appliances', label: 'Appliances to Clean', type: 'multi-select', options: APPLIANCE_OPTIONS, required: false },
  { id: 'areas', label: 'Extra Areas', type: 'multi-select', options: EXTRA_AREAS_OPTIONS, required: false },
  { id: 'linen_preference', label: 'Laundry & Linen Service', type: 'select', options: LAUNDRY_OPTIONS, required: false },
  { id: 'treatments', label: 'Special Treatments Needed', type: 'multi-select', options: TREATMENT_OPTIONS, required: false },
  { id: 'parking', label: 'Parking Availability', type: 'select', options: PARKING_OPTIONS, required: false },
  { id: 'cleaning_cupboard', label: 'Cleaning Cupboard', type: 'select', options: CLEANING_CUPBOARD_OPTIONS, required: false },
  { id: 'waste_sorting', label: 'Waste Sorting', type: 'select', options: WASTE_SORTING_OPTIONS, required: false },
  { id: 'restocking_preference', label: 'Restocking Preference', type: 'select', options: RESTOCKING_OPTIONS, required: false }
];

// Type definitions
interface FormData {
  service_category: string;
  property_type: string;
  service_type: string;
  airbnb_service_type: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  postcode: string;
  company: string;
  details: Record<string, any>;
}

interface ServiceCategory {
  id: number;
  label: string;
  icon?: string;
}

interface ServiceType {
  id: number | string;
  label: string;
  name?: string;
  icon?: string;
  property_id?: string;
  description?: string;
  service_type_id?: string;
}

// Default questions for categories 24, 25, 26 (Specialist & Compliance, Waste & Clearance, Windows/Doors/Glazing)
const SPECIALIST_COMPLIANCE_DEFAULT_QUESTIONS = [
  { id: 'service_scope', label: 'What is the scope of work?', type: 'select', options: [
    { id: 'assessment', label: 'Assessment/Audit' },
    { id: 'implementation', label: 'Implementation/Remediation' },
    { id: 'both', label: 'Both assessment and implementation' }
  ], required: true, field_name: 'service_scope' },
  { id: 'timeline', label: 'When do you need this completed?', type: 'select', options: [
    { id: 'urgent', label: 'Urgent (within 1 week)' },
    { id: 'soon', label: 'Soon (1-2 weeks)' },
    { id: 'flexible', label: 'Flexible timeline' }
  ], required: true, field_name: 'timeline' }
];

const WASTE_CLEARANCE_DEFAULT_QUESTIONS = [
  { id: 'waste_type', label: 'What type of waste needs to be cleared?', type: 'select', options: [
    { id: 'general', label: 'General household waste' },
    { id: 'garden', label: 'Garden and landscaping waste' },
    { id: 'construction', label: 'Construction waste' },
    { id: 'mixed', label: 'Mixed waste' }
  ], required: true, field_name: 'waste_type' },
  { id: 'volume', label: 'Approximately how much waste?', type: 'select', options: [
    { id: 'small', label: 'Small load (van)' },
    { id: 'medium', label: 'Medium load (small skip)' },
    { id: 'large', label: 'Large load (large skip)' }
  ], required: true, field_name: 'volume' }
];

const WINDOWS_DOORS_GLAZING_DEFAULT_QUESTIONS = [
  { id: 'work_type', label: 'What type of work is needed?', type: 'select', options: [
    { id: 'new_installation', label: 'New installation' },
    { id: 'replacement', label: 'Replacement' },
    { id: 'repair', label: 'Repair' },
    { id: 'survey', label: 'Survey/quotation only' }
  ], required: true, field_name: 'work_type' },
  { id: 'material_preference', label: 'Material preference?', type: 'select', options: [
    { id: 'upvc', label: 'UPVC/Plastic' },
    { id: 'aluminium', label: 'Aluminium' },
    { id: 'timber', label: 'Timber' },
    { id: 'no_preference', label: 'No preference' }
  ], required: true, field_name: 'material_preference' }
];

// Roofing default questions
const ROOFING_DEFAULT_QUESTIONS = [
  { id: 'roof_condition', label: 'What is the current condition of the roof?', type: 'select', options: [
    { id: 'new', label: 'New/Good condition' },
    { id: 'minor_damage', label: 'Minor damage/wear' },
    { id: 'significant_damage', label: 'Significant damage' },
    { id: 'emergency', label: 'Emergency/Urgent repair needed' }
  ], required: true, field_name: 'roof_condition' },
  { id: 'roof_area', label: 'Approximate roof area?', type: 'select', options: [
    { id: 'small', label: 'Small (under 50 sqm)' },
    { id: 'medium', label: 'Medium (50-100 sqm)' },
    { id: 'large', label: 'Large (100-200 sqm)' },
    { id: 'very_large', label: 'Very large (over 200 sqm)' }
  ], required: true, field_name: 'roof_area' },
  { id: 'roof_access', label: 'How accessible is the roof?', type: 'select', options: [
    { id: 'easy', label: 'Easy access (minimal scaffolding)' },
    { id: 'moderate', label: 'Moderate access (some scaffolding)' },
    { id: 'difficult', label: 'Difficult access (extensive scaffolding)' },
    { id: 'very_difficult', label: 'Very difficult/tight access' }
  ], required: true, field_name: 'roof_access' }
];

// Property Maintenance default questions
const PROPERTY_MAINTENANCE_DEFAULT_QUESTIONS = [
  { id: 'maintenance_type', label: 'What type of maintenance is needed?', type: 'select', options: [
    { id: 'regular', label: 'Regular/Planned maintenance' },
    { id: 'urgent', label: 'Urgent repair' },
    { id: 'inspection', label: 'Property inspection/assessment' },
    { id: 'mixed', label: 'Mix of tasks' }
  ], required: true, field_name: 'maintenance_type' },
  { id: 'property_size', label: 'What is the property size?', type: 'select', options: [
    { id: 'small', label: 'Small (studio/1bed)' },
    { id: 'medium', label: 'Medium (2-3 bed)' },
    { id: 'large', label: 'Large (4+ bed)' },
    { id: 'commercial', label: 'Commercial/Large building' }
  ], required: true, field_name: 'property_size' },
  { id: 'access', label: 'How accessible is the property?', type: 'select', options: [
    { id: 'easy', label: 'Easy access' },
    { id: 'moderate', label: 'Moderate access' },
    { id: 'difficult', label: 'Difficult access' }
  ], required: true, field_name: 'access' }
];

// Locks & Security default questions
const LOCKS_SECURITY_DEFAULT_QUESTIONS = [
  { id: 'security_type', label: 'What type of security work is needed?', type: 'select', options: [
    { id: 'new_install', label: 'New installation' },
    { id: 'upgrade', label: 'System upgrade' },
    { id: 'repair', label: 'Repair/replacement' },
    { id: 'audit', label: 'Security audit/assessment' }
  ], required: true, field_name: 'security_type' },
  { id: 'property_type', label: 'What type of property?', type: 'select', options: [
    { id: 'residential', label: 'Residential' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'industrial', label: 'Industrial' }
  ], required: true, field_name: 'property_type' },
  { id: 'budget', label: 'Do you have a budget in mind?', type: 'select', options: [
    { id: 'under_1k', label: 'Under £1,000' },
    { id: '1k_5k', label: '£1,000 - £5,000' },
    { id: '5k_plus', label: 'Over £5,000' },
    { id: 'flexible', label: 'Flexible' }
  ], required: true, field_name: 'budget' }
];

// Plumbing default questions
const PLUMBING_DEFAULT_QUESTIONS = [
  { id: 'work_type', label: 'What type of plumbing work is needed?', type: 'select', options: [
    { id: 'installation', label: 'New installation' },
    { id: 'repair', label: 'Repair' },
    { id: 'replacement', label: 'System replacement' },
    { id: 'maintenance', label: 'Maintenance/inspection' }
  ], required: true, field_name: 'work_type' },
  { id: 'urgency', label: 'How urgent is this work?', type: 'select', options: [
    { id: 'emergency', label: 'Emergency (within 24 hours)' },
    { id: 'urgent', label: 'Urgent (within 1 week)' },
    { id: 'soon', label: 'Soon (within 2-4 weeks)' },
    { id: 'flexible', label: 'Flexible' }
  ], required: true, field_name: 'urgency' },
  { id: 'access', label: 'How accessible is the location?', type: 'select', options: [
    { id: 'easy', label: 'Easy access' },
    { id: 'moderate', label: 'Moderate access' },
    { id: 'difficult', label: 'Difficult access' }
  ], required: true, field_name: 'access' }
];

// Finishing & Interiors default questions
const FINISHING_INTERIORS_DEFAULT_QUESTIONS = [
  { id: 'project_scope', label: 'What is the scope of your finishing/interior project?', type: 'select', options: [
    { id: 'full_room', label: 'Full room decoration' },
    { id: 'partial', label: 'Partial/accent work' },
    { id: 'surfaces', label: 'Wall/floor/ceiling surfaces' },
    { id: 'custom', label: 'Custom features' }
  ], required: true, field_name: 'project_scope' },
  { id: 'materials', label: 'What materials are preferred?', type: 'select', options: [
    { id: 'standard', label: 'Standard materials' },
    { id: 'premium', label: 'Premium/high-end materials' },
    { id: 'specialist', label: 'Specialist/bespoke materials' },
    { id: 'no_preference', label: 'No preference' }
  ], required: true, field_name: 'materials' },
  { id: 'timeline', label: 'When is your completion date?', type: 'select', options: [
    { id: 'urgent', label: 'Urgent (1-2 weeks)' },
    { id: 'soon', label: 'Soon (1 month)' },
    { id: 'flexible', label: 'Flexible' }
  ], required: true, field_name: 'timeline' }
];

// Kitchens & Bathrooms default questions
const KITCHENS_BATHROOMS_DEFAULT_QUESTIONS = [
  { id: 'project_type', label: 'What type of project?', type: 'select', options: [
    { id: 'new_install', label: 'New installation' },
    { id: 'complete_refit', label: 'Complete refit' },
    { id: 'partial_upgrade', label: 'Partial upgrade' },
    { id: 'repair', label: 'Repair/replacement' }
  ], required: true, field_name: 'project_type' },
  { id: 'room_type', label: 'Is this for kitchen, bathroom, or both?', type: 'select', options: [
    { id: 'kitchen', label: 'Kitchen' },
    { id: 'bathroom', label: 'Bathroom' },
    { id: 'both', label: 'Both kitchen and bathroom' }
  ], required: true, field_name: 'room_type' },
  { id: 'timeline', label: 'What is your project timeline?', type: 'select', options: [
    { id: 'urgent', label: 'Urgent (1-2 weeks)' },
    { id: 'soon', label: 'Soon (1-2 months)' },
    { id: 'flexible', label: 'Flexible' }
  ], required: true, field_name: 'timeline' }
];

const ServiceRequestPage = () => {
  const [step, setStep] = useState<string>('category');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionData, setSubmissionData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(false);
  const [isLoadingServiceTypes, setIsLoadingServiceTypes] = useState<boolean>(false);
  const [openMultiSelect, setOpenMultiSelect] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Utility function to deduplicate service types by ID and label
  const deduplicateServices = (services: any[]) => {
    if (!Array.isArray(services)) return [];
    const seen = new Map();
    return services.filter((svc) => {
      const key = `${svc.id || svc.service_type_id}-${svc.label || svc.name}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  };

  // Database-fetched data
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [dynamicQuestions, setDynamicQuestions] = useState<any[]>([]);
  const [coreConstructionQuestions, setCoreConstructionQuestions] = useState<any[]>([]);
  const [coreConstructionEstimate, setCoreConstructionEstimate] = useState<any>(null);
  const [carpentryQuestions, setCarpentryQuestions] = useState<any[]>([]);
  const [carpentryEstimate, setCarpentryEstimate] = useState<any>(null);
  const [electricalQuestions, setElectricalQuestions] = useState<any[]>([]);
  const [electricalEstimate, setElectricalEstimate] = useState<any>(null);
  const [landscapingQuestions, setLandscapingQuestions] = useState<any[]>([]);
  const [landscapingEstimate, setLandscapingEstimate] = useState<any>(null);
  const [finishingInteriorsQuestions, setFinishingInteriorsQuestions] = useState<any[]>([]);
  const [finishingInteriorsEstimate, setFinishingInteriorsEstimate] = useState<any>(null);
  const [kitchensBathroomsQuestions, setKitchensBathroomsQuestions] = useState<any[]>([]);
  const [kitchensBathroomsEstimate, setKitchensBathroomsEstimate] = useState<any>(null);
  const [locksSecurityQuestions, setLocksSecurityQuestions] = useState<any[]>([]);
  const [locksSecurityEstimate, setLocksSecurityEstimate] = useState<any>(null);
  const [plumbingQuestions, setPlumbingQuestions] = useState<any[]>([]);
  const [plumbingEstimate, setPlumbingEstimate] = useState<any>(null);
  const [propertyMaintenanceQuestions, setPropertyMaintenanceQuestions] = useState<any[]>([]);
  const [propertyMaintenanceEstimate, setPropertyMaintenanceEstimate] = useState<any>(null);
  const [roofingQuestions, setRoofingQuestions] = useState<any[]>([]);
  const [roofingEstimate, setRoofingEstimate] = useState<any>(null);
  const [specialistComplianceQuestions, setSpecialistComplianceQuestions] = useState<any[]>([]);
  const [specialistComplianceEstimate, setSpecialistComplianceEstimate] = useState<any>(null);
  const [wasteClearanceQuestions, setWasteClearanceQuestions] = useState<any[]>([]);
  const [wasteClearanceEstimate, setWasteClearanceEstimate] = useState<any>(null);

  const [formData, setFormData] = useState<FormData>({
    service_category: '',
    property_type: '',
    service_type: '',
    airbnb_service_type: '',
    contact_name: user?.contact_name || '',
    contact_email: user?.email || '',
    contact_phone: user?.phone || '',
    postcode: '',
    company: '',
    details: {}
  });

  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateDetail = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      details: { ...prev.details, [field]: value }
    }));
  };

  // Validate that all required questions have been answered
  const validateRequiredQuestions = (): boolean => {
    // Get the questions array for current step (same logic as renderQuestionsStep)
    let questionsToValidate: any[] = [];
    const propertyType = String(formData.property_type);
    const categoryNum = Number(formData.service_category);
    
    // Map current step to category or property type
    if (step === 'questions' || step === 'cleaningCategoryStep') {
      // Generic questions - use property type
      if (propertyType === 'event') questionsToValidate = EVENT_VENUE_QUESTIONS;
      else if (propertyType === 'hmo') questionsToValidate = HMO_QUESTIONS;
      else if (propertyType === 'residential') questionsToValidate = RESIDENTIAL_QUESTIONS;
      else if (propertyType === 'specialist') questionsToValidate = SPECIALIST_CLEANING_QUESTIONS;
      else if (propertyType === 'commercial') questionsToValidate = COMMERCIAL_QUESTIONS;
      else if (propertyType === 'carpentry') questionsToValidate = carpentryQuestions;
      else questionsToValidate = dynamicQuestions;
    } else if (step === 'coreConstructionQuestions') questionsToValidate = coreConstructionQuestions;
    else if (step === 'electricalQuestions') questionsToValidate = electricalQuestions;
    else if (step === 'landscapingQuestions') questionsToValidate = landscapingQuestions;
    else if (step === 'finishingInteriorsQuestions') questionsToValidate = finishingInteriorsQuestions;
    else if (step === 'kitchensBathroomsQuestions') questionsToValidate = kitchensBathroomsQuestions;
    else if (step === 'locksSecurityQuestions') questionsToValidate = locksSecurityQuestions;
    else if (step === 'plumbingQuestions') questionsToValidate = plumbingQuestions;
    else if (step === 'propertyMaintenanceQuestions') questionsToValidate = propertyMaintenanceQuestions;
    else if (step === 'roofingQuestions') questionsToValidate = roofingQuestions;
    else if (step === 'specialistComplianceQuestions') questionsToValidate = specialistComplianceQuestions;
    else if (step === 'wasteClearanceQuestions') questionsToValidate = wasteClearanceQuestions;
    else if (step === 'windowsDoorsGlazingQuestions') questionsToValidate = dynamicQuestions;
    
    // Validate required questions
    for (const question of questionsToValidate) {
      // Skip questions with conditionals that don't apply
      if (question.conditional) {
        const [field, value] = question.conditional.split(':');
        if (field === 'service_type') {
          const shouldShow = formData.service_type === value || formData.service_type?.includes(value);
          if (!shouldShow) continue; // Skip this question
        }
      }
      
      if (question.required) {
        const fieldName = question.field_name || question.id;
        const value = formData.details[fieldName];
        
        // Check if value is empty
        if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')) {
          toast({
            title: 'Incomplete Form',
            description: `Please answer: ${question.label}`,
            variant: 'destructive'
          });
          return false;
        }
      }
    }
    return true;
  };

  // FORCE REDIRECT: If property_type is 'specialist', always go to specialistUnavailable
  useEffect(() => {
    if (formData.property_type === 'specialist' && step !== 'specialistUnavailable') {
      console.log('Force redirect: property_type is specialist, redirecting to specialistUnavailable');
      setStep('specialistUnavailable');
    }
  }, [formData.property_type, step]);

  // Handle going back
  const handleGoBack = () => {
    switch (step) {
      case 'serviceType':
        setFormData(prev => ({
          ...prev,
          service_category: '',
          property_type: '',
          service_type: '',
          airbnb_service_type: '',
          details: {}
        }));
        setDynamicQuestions([]);
        setServiceTypes([]);
        setStep('category');
        break;
      
      // ======== SPECIALIZED SERVICE TYPE STEPS ========
      // These should NOT clear form data - only reset service_type and details
      // and go back to category (NOT serviceType) to maintain clean state
      case 'airbnbService':
      case 'cleaningServiceType':
      case 'eventVenueServiceType':
      case 'hmoServiceType':
      case 'specialistCleaningServiceType':
      case 'commercialServiceType':
        // Cleaning-related service types: clear only service_type and details
        setFormData(prev => ({
          ...prev,
          service_type: '',
          airbnb_service_type: '',
          details: {}
        }));
        setDynamicQuestions([]);
        setStep('category');
        break;
      
      // Specialized service types for non-cleaning categories: go back to CATEGORY
      // NOT serviceType - to avoid showing duplicate service types from renderServiceTypeStep
      case 'carpentryServiceType':
      case 'coreConstructionServiceType':
      case 'electricalServiceType':
      case 'landscapingServiceType':
      case 'finishingInteriorsServiceType':
      case 'kitchensBathroomsServiceType':
      case 'locksSecurityServiceType':
      case 'plumbingServiceType':
      case 'propertyMaintenanceServiceType':
      case 'roofingServiceType':
      case 'specialistComplianceServiceType':
      case 'wasteClearanceServiceType':
      case 'windowsDoorsGlazingServiceType':
        // Clear everything when going back from specialized service type
        setFormData(prev => ({
          ...prev,
          service_type: '',
          service_category: '',
          details: {}
        }));
        setServiceTypes([]);
        setDynamicQuestions([]);
        setStep('category');
        break;
      
      case 'airbnbDetails':
        setFormData(prev => ({ ...prev, details: {} }));
        setStep('airbnbService');
        break;
      
      case 'hotelHousekeepingMessage':
        setStep('airbnbService');
        break;
      
      case 'cleaningCategoryStep':
        // Going back from cleaning service selection (Category 1) - return to category selection
        setFormData(prev => ({
          ...prev,
          service_category: '',
          service_type: '',
          property_type: '',
          details: {}
        }));
        setDynamicQuestions([]);
        setStep('category');
        break;
      
      case 'specialistUnavailable':
        setStep('specialistCleaningServiceType');
        break;
      
      case 'contact':
        setStep('postcode');
        break;
      
      case 'questions':
        const isCleaningQuestions = Number(formData.service_category) === 6 || Number(formData.service_category) === 1;
        const isAirbnbQuestions = formData.property_type === 'airbnb';
        const isCommercialQuestions = formData.property_type === 'commercial';
        const isEventVenueQuestions = formData.property_type === 'event';
        const isHmoQuestions = formData.property_type === 'hmo';
        const isSpecialistQuestions = formData.property_type === 'specialist';
        const isCarpentryQuestions = formData.property_type === 'carpentry';

        setDynamicQuestions([]);

        if (isCleaningQuestions && isAirbnbQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('airbnbService');
        } else if (isCleaningQuestions && isCommercialQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('commercialServiceType');
        } else if (isCleaningQuestions && isEventVenueQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('eventVenueServiceType');
        } else if (isCleaningQuestions && isHmoQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('hmoServiceType');
        } else if (isCleaningQuestions && isSpecialistQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('specialistCleaningServiceType');
        } else if (isCleaningQuestions && Number(formData.service_category) === 1) {
          // Category 1 (Cleaning) - go back to cleaning service selection
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('cleaningCategoryStep');
        } else if (isCleaningQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('cleaningServiceType');
        } else if (isCarpentryQuestions) {
          setFormData(prev => ({ ...prev, details: {} }));
          setStep('carpentryServiceType');
        } else {
          setFormData(prev => ({ ...prev, service_type: '', details: {} }));
          setStep('serviceType');
        }
        break;
      
      case 'electricalQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('electricalServiceType');
        break;
      
      case 'landscapingQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('landscapingServiceType');
        break;
      
      case 'coreConstructionQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('coreConstructionServiceType');
        break;
      
      case 'finishingInteriorsQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('finishingInteriorsServiceType');
        break;
      
      case 'kitchensBathroomsQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('kitchensBathroomsServiceType');
        break;
      
      case 'locksSecurityQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('locksSecurityServiceType');
        break;
      
      case 'plumbingQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('plumbingServiceType');
        break;
      
      case 'propertyMaintenanceQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('propertyMaintenanceServiceType');
        break;
      
      case 'roofingQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('roofingServiceType');
        break;
      
      case 'specialistComplianceQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('specialistComplianceServiceType');
        break;
      
      case 'wasteClearanceQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('wasteClearanceServiceType');
        break;
      
      case 'windowsDoorsGlazingQuestions':
        setFormData(prev => ({ ...prev, details: {} }));
        setDynamicQuestions([]);
        setStep('windowsDoorsGlazingServiceType');
        break;

      case 'postcode':
        // Route back to the correct step based on category
        const categoryId = Number(formData.service_category);
        if (categoryId === 1) {
          // Category 1 (Cleaning) - go back to questions
          setStep('questions');
        } else if (categoryId === 15) {
          setStep('coreConstructionQuestions');
        } else if (categoryId === 16) {
          setStep('landscapingQuestions');
        } else if (categoryId === 17) {
          setStep('electricalQuestions');
        } else if (categoryId === 18) {
          setStep('finishingInteriorsQuestions');
        } else if (categoryId === 19) {
          setStep('kitchensBathroomsQuestions');
        } else if (categoryId === 20) {
          setStep('locksSecurityQuestions');
        } else if (categoryId === 21) {
          setStep('plumbingQuestions');
        } else if (categoryId === 22) {
          setStep('propertyMaintenanceQuestions');
        } else if (categoryId === 23) {
          setStep('roofingQuestions');
        } else if (categoryId === 24) {
          setStep('specialistComplianceQuestions');
        } else if (categoryId === 25) {
          setStep('wasteClearanceQuestions');
        } else if (categoryId === 26) {
          setStep('windowsDoorsGlazingQuestions');
        } else {
          setStep('questions');
        }
        break;
      
      default:
        break;
    }
  };

  // Debug: Log step changes  
  useEffect(() => {
    console.log(`[STEP-CHANGE] step is now: "${step}" | service_category: ${formData.service_category}`);
    console.log(new Error().stack);
  }, [step]);

  // Fetch service categories
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingData(true);

        // Try new public endpoint first, fall back to old endpoint if needed
        let categoriesRes;
        try {
          const response = await fetch('/api/public/service-categories');
          categoriesRes = await response.json();
        } catch (err) {
          console.warn('New public endpoint failed, trying fallback:', err);
          const response = await fetch('/api/services/categories');
          categoriesRes = await response.json();
        }

        let propertiesRes;
        try {
          const response = await fetch('/api/services/properties');
          propertiesRes = await response.json();
        } catch (err) {
          console.warn('Properties endpoint failed:', err);
          propertiesRes = { success: true, data: [] };
        }

        // Transform categories to match expected format
        if (categoriesRes.success && Array.isArray(categoriesRes.data)) {
          // Filter out unwanted categories - keep only "Cleaning & Care", remove plain "Cleaning"
          const excludedCategories = ['Cleaning', 'Logistics', 'Maintenance', 'Removals'];
          const transformedCategories = categoriesRes.data
            .filter((cat: any) => !excludedCategories.includes(cat.name))
            .map((cat: any) => ({
              id: cat.id,
              label: cat.name,
              description: cat.description,
              icon: cat.icon,
              slug: cat.slug,
              service_count: cat.service_count || 0
            }));
          setServiceCategories(transformedCategories);
        }

        if (propertiesRes.success && Array.isArray(propertiesRes.data)) {
          setPropertyTypes(propertiesRes.data);
        }
      } catch (error: any) {
        console.error('Error fetching initial data:', error);
        toast({ title: 'Error', description: 'Failed to load form data', variant: 'destructive' });
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchInitialData();
  }, [toast]);

  // Fetch service types when category changes
  useEffect(() => {
    const categoryId = Number(formData.service_category);
    console.log(`[useEffect-SERVICE-TYPES] Running: categoryId=${categoryId}, step=${step}, formData.service_category=${formData.service_category}`);
    
    // Categories with dedicated handlers that should NOT fetch from API:
    // 6=Cleaning (uses property types), 20=Locks, 21=Plumbing, 22=Property Maintenance, 23=Roofing
    // 14=Carpentry, 15=Core Construction, 16=Landscaping, 17=Electrical, 18=Finishing Interiors
    // (these use hardcoded constant arrays)
    const skipFetchCategories = [6, 20, 21, 22, 23];
    const useConstantCategories = [14, 15, 16, 17, 18];
    const shouldSkipFetch = (categoryId === 24 || categoryId === 25 || categoryId === 26) && step === 'serviceType';

    // Handle categories with hardcoded constants FIRST (before checking skipFetchCategories)
    if (formData.service_category && useConstantCategories.includes(categoryId)) {
      console.log(`[useEffect-SERVICE-TYPES] ✓ categoryId ${categoryId} is in useConstantCategories`);
      if (categoryId === 14) {
        // Carpentry & Joinery - use hardcoded constant
        console.log(`[useEffect-SERVICE-TYPES] Setting CARPENTRY_SERVICE_TYPES (${CARPENTRY_SERVICE_TYPES.length} services)`);
        setServiceTypes(CARPENTRY_SERVICE_TYPES);
      } else if (categoryId === 15) {
        // Core Construction & Structural - use hardcoded constant
        console.log(`[useEffect-SERVICE-TYPES] Setting CORE_CONSTRUCTION_SERVICE_TYPES`);
        setServiceTypes(CORE_CONSTRUCTION_SERVICE_TYPES);
      } else if (categoryId === 16) {
        // External Works & Landscaping - use hardcoded constant
        console.log(`[useEffect-SERVICE-TYPES] Setting LANDSCAPING_SERVICE_TYPES`);
        setServiceTypes(LANDSCAPING_SERVICE_TYPES);
      } else if (categoryId === 17) {
        // Electrical & Smart Systems - use hardcoded constant
        console.log(`[useEffect-SERVICE-TYPES] Setting ELECTRICAL_SERVICE_TYPES`);
        setServiceTypes(ELECTRICAL_SERVICE_TYPES);
      } else if (categoryId === 18) {
        // Finishing & Interiors - use hardcoded constant
        console.log(`[useEffect-SERVICE-TYPES] Setting FINISHING_INTERIORS_SERVICE_TYPES`);
        setServiceTypes(FINISHING_INTERIORS_SERVICE_TYPES);
      }
      console.log(`[useEffect-SERVICE-TYPES] ✓ Exiting early - return`);
      return; // Exit early - don't run the rest
    }

    if (formData.service_category && !shouldSkipFetch && !skipFetchCategories.includes(categoryId) && (step === 'serviceType' || step === 'category' || step === 'specialistComplianceServiceType' || step === 'wasteClearanceServiceType' || step === 'windowsDoorsGlazingServiceType')) {
      const selectedCategory = serviceCategories.find(c => c.id === categoryId);
      const isCleaning = selectedCategory?.label?.toLowerCase().includes('cleaning');
      
      // Check if this is one of the new pricing service categories
      if (categoryId === 20) {
        // Locks & Security
        setServiceTypes(LOCKS_SECURITY_SERVICE_TYPES);
      } else if (categoryId === 21) {
        // Plumbing, Heating & Gas
        setServiceTypes(PLUMBING_SERVICE_TYPES);
      } else if (categoryId === 22) {
        // Property Maintenance & Handyman
        setServiceTypes(PROPERTY_MAINTENANCE_SERVICE_TYPES);
      } else if (categoryId === 23) {
        // Roofing
        setServiceTypes(ROOFING_SERVICE_TYPES);
      } else if (categoryId === 24 || categoryId === 25 || categoryId === 26) {
        // Specialist & Compliance (24), Waste & Clearance (25), Windows/Doors/Glazing (26)
        // Only fetch when on their dedicated steps, not on generic serviceType
        const fetchServiceTypes = async () => {
          try {
            // Try public endpoint first
            let response = await fetch(`/api/public/services/by-category/${categoryId}`);
            let data = await response.json();

            // Fallback to old endpoint if needed
            if (!data.success) {
              response = await fetch(`/api/services/types?category_id=${categoryId}`);
              data = await response.json();
            }

            if (data.success && Array.isArray(data.data)) {
              const deduped = deduplicateServices(data.data);
              setServiceTypes(deduped);
            } else {
              setServiceTypes([]);
            }
          } catch (error) {
            setServiceTypes([]);
          }
        };
        fetchServiceTypes();
      } else if (isCleaning) {
        setServiceTypes(DEFAULT_PROPERTY_TYPES);
      } else {
        const fetchServiceTypes = async () => {
          try {
            // Try public endpoint first
            let response = await fetch(`/api/public/services/by-category/${formData.service_category}`);
            let data = await response.json();

            // Fallback to old endpoint if needed
            if (!data.success) {
              response = await fetch(`/api/services/types?category_id=${formData.service_category}`);
              data = await response.json();
            }

            if (data.success && Array.isArray(data.data)) {
              setServiceTypes(data.data);
            } else {
              setServiceTypes([]);
            }
          } catch (error) {
            console.error('Error fetching service types:', error);
            setServiceTypes([]);
          }
        };
        fetchServiceTypes();
      }
    }
  }, [formData.service_category, step]);

  // Fetch cleaning service types
  useEffect(() => {
    if (formData.property_type && Number(formData.service_category) === 6 && 
        (step === 'cleaningServiceType' || step === 'commercialServiceType') && 
        formData.property_type !== 'specialist') {
      const fetchCleaningServiceTypes = async () => {
        setIsLoadingServiceTypes(true);
        try {
          const selectedProperty = DEFAULT_PROPERTY_TYPES.find(p => p.id.toString() === formData.property_type.toString());
          const propertyId = selectedProperty?.property_id || formData.property_type;
          
          const response = await fetch(`/api/services/types?category_id=6&property_id=${propertyId}`);
          const data = await response.json();
          
          if (data.success && Array.isArray(data.data) && data.data.length > 0) {
            const deduped = deduplicateServices(data.data);
            setServiceTypes(deduped);
          } else {
            if (propertyId === 'residential') {
              console.log('[CLEANING] Using RESIDENTIAL_SERVICE_TYPES_FALLBACK');
              setServiceTypes(RESIDENTIAL_SERVICE_TYPES_FALLBACK);
            } else if (propertyId === 'commercial') {
              console.log('[CLEANING] Using COMMERCIAL_SERVICE_TYPES_FALLBACK');
              setServiceTypes(COMMERCIAL_SERVICE_TYPES_FALLBACK);
            } else {
              setServiceTypes([]);
            }
          }
        } catch (error) {
          console.error('Error fetching cleaning service types:', error);
          const selectedProperty = DEFAULT_PROPERTY_TYPES.find(p => p.id.toString() === formData.property_type.toString());
          const propertyId = selectedProperty?.property_id || formData.property_type;
          if (propertyId === 'residential') {
            setServiceTypes(RESIDENTIAL_SERVICE_TYPES_FALLBACK);
          } else if (propertyId === 'commercial') {
            setServiceTypes(COMMERCIAL_SERVICE_TYPES_FALLBACK);
          } else {
            setServiceTypes([]);
          }
        } finally {
          setIsLoadingServiceTypes(false);
        }
      };
      fetchCleaningServiceTypes();
    }
  }, [formData.property_type, formData.service_category, step]);

  // Fetch Carpentry questions from database when step changes to questions
  useEffect(() => {
    if (step === 'questions' && formData.property_type === 'carpentry' && formData.service_type) {
      const fetchCarpentryQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[CARPENTRY] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[CARPENTRY] Loaded ${response.data.length} questions from database`);
            setCarpentryQuestions(response.data);
          } else {
            console.error('[CARPENTRY] Failed to fetch questions:', response.message);
            // Fallback to empty array - let renderQuestionsStep handle the fallback
            setCarpentryQuestions([]);
          }
        } catch (error) {
          console.error('[CARPENTRY] Error fetching questions:', error);
          setCarpentryQuestions([]);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchCarpentryQuestions();
    }
  }, [step, formData.property_type, formData.service_type]);

  // Fetch Core Construction questions from database when step changes to coreConstructionQuestions
  useEffect(() => {
    if (step === 'coreConstructionQuestions' && formData.service_type) {
      const fetchCoreConstructionQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[CORE-CONSTRUCTION] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[CORE-CONSTRUCTION] Loaded ${response.data.length} questions from database`);
            setCoreConstructionQuestions(response.data);
          } else {
            console.error('[CORE-CONSTRUCTION] Failed to fetch questions:', response.message);
            // Keep the hardcoded defaults as fallback
            const defaultCoreConstructionQuestions = [
              {
                id: 'project_scope',
                label: 'Please describe your project scope and requirements',
                type: 'textarea',
                required: true,
                field_name: 'project_scope'
              },
              {
                id: 'site_access',
                label: 'How accessible is the construction site?',
                type: 'select',
                options: [
                  { id: 'easy', label: 'Easy access - clear site' },
                  { id: 'moderate', label: 'Moderate access - some obstacles' },
                  { id: 'difficult', label: 'Difficult access - restricted' }
                ],
                required: true,
                field_name: 'site_access'
              },
              {
                id: 'timeline',
                label: 'What is your project timeline?',
                type: 'select',
                options: [
                  { id: 'urgent', label: 'Urgent (within 1 week)' },
                  { id: 'soon', label: 'Soon (1-3 weeks)' },
                  { id: 'flexible', label: 'Flexible timeline' }
                ],
                required: true,
                field_name: 'timeline'
              },
              {
                id: 'budget_range',
                label: 'Do you have a budget range in mind?',
                type: 'select',
                options: [
                  { id: 'under_5k', label: 'Under £5,000' },
                  { id: '5k_15k', label: '£5,000 - £15,000' },
                  { id: '15k_50k', label: '£15,000 - £50,000' },
                  { id: 'over_50k', label: 'Over £50,000' },
                  { id: 'not_sure', label: 'Not sure' }
                ],
                required: false,
                field_name: 'budget_range'
              }
            ];
            setCoreConstructionQuestions(defaultCoreConstructionQuestions);
          }
        } catch (error) {
          console.error('[CORE-CONSTRUCTION] Error fetching questions:', error);
          const defaultCoreConstructionQuestions = [
            {
              id: 'project_scope',
              label: 'Please describe your project scope and requirements',
              type: 'textarea',
              required: true,
              field_name: 'project_scope'
            },
            {
              id: 'site_access',
              label: 'How accessible is the construction site?',
              type: 'select',
              options: [
                { id: 'easy', label: 'Easy access - clear site' },
                { id: 'moderate', label: 'Moderate access - some obstacles' },
                { id: 'difficult', label: 'Difficult access - restricted' }
              ],
              required: true,
              field_name: 'site_access'
            },
            {
              id: 'timeline',
              label: 'What is your project timeline?',
              type: 'select',
              options: [
                { id: 'urgent', label: 'Urgent (within 1 week)' },
                { id: 'soon', label: 'Soon (1-3 weeks)' },
                { id: 'flexible', label: 'Flexible timeline' }
              ],
              required: true,
              field_name: 'timeline'
            },
            {
              id: 'budget_range',
              label: 'Do you have a budget range in mind?',
              type: 'select',
              options: [
                { id: 'under_5k', label: 'Under £5,000' },
                { id: '5k_15k', label: '£5,000 - £15,000' },
                { id: '15k_50k', label: '£15,000 - £50,000' },
                { id: 'over_50k', label: 'Over £50,000' },
                { id: 'not_sure', label: 'Not sure' }
              ],
              required: false,
              field_name: 'budget_range'
            }
          ];
          setCoreConstructionQuestions(defaultCoreConstructionQuestions);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchCoreConstructionQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Electrical questions from database when step changes to electricalQuestions
  useEffect(() => {
    if (step === 'electricalQuestions' && formData.service_type) {
      const fetchElectricalQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[ELECTRICAL] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[ELECTRICAL] Loaded ${response.data.length} questions from database`);
            setElectricalQuestions(response.data);
          } else {
            console.error('[ELECTRICAL] Failed to fetch questions:', response.message);
            setElectricalQuestions([]);
          }
        } catch (error) {
          console.error('[ELECTRICAL] Error fetching questions:', error);
          setElectricalQuestions([]);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchElectricalQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Landscaping questions from database when step changes to landscapingQuestions
  useEffect(() => {
    if (step === 'landscapingQuestions' && formData.service_type) {
      const fetchLandscapingQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[LANDSCAPING] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[LANDSCAPING] Loaded ${response.data.length} questions from database`);
            setLandscapingQuestions(response.data);
          } else {
            console.error('[LANDSCAPING] Failed to fetch questions:', response.message);
            setLandscapingQuestions([]);
          }
        } catch (error) {
          console.error('[LANDSCAPING] Error fetching questions:', error);
          setLandscapingQuestions([]);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchLandscapingQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Finishing & Interiors questions from database
  useEffect(() => {
    if (step === 'finishingInteriorsQuestions' && formData.service_type) {
      const fetchFinishingQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[FINISHING_INTERIORS] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[FINISHING_INTERIORS] Loaded ${response.data.length} questions from database`);
            setFinishingInteriorsQuestions(response.data);
          } else {
            console.error('[FINISHING_INTERIORS] Failed to fetch questions:', response.message);
            setFinishingInteriorsQuestions(FINISHING_INTERIORS_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[FINISHING_INTERIORS] Error fetching questions:', error);
          setFinishingInteriorsQuestions(FINISHING_INTERIORS_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchFinishingQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Kitchens & Bathrooms questions from database
  useEffect(() => {
    if (step === 'kitchensBathroomsQuestions' && formData.service_type) {
      const fetchKitchensBathroomsQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[KITCHENS_BATHROOMS] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[KITCHENS_BATHROOMS] Loaded ${response.data.length} questions from database`);
            setKitchensBathroomsQuestions(response.data);
          } else {
            console.error('[KITCHENS_BATHROOMS] Failed to fetch questions:', response.message);
            setKitchensBathroomsQuestions(KITCHENS_BATHROOMS_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[KITCHENS_BATHROOMS] Error fetching questions:', error);
          setKitchensBathroomsQuestions(KITCHENS_BATHROOMS_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchKitchensBathroomsQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Locks & Security questions from database
  useEffect(() => {
    if (step === 'locksSecurityQuestions' && formData.service_type) {
      const fetchLocksSecurityQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[LOCKS_SECURITY] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[LOCKS_SECURITY] Loaded ${response.data.length} questions from database`);
            setLocksSecurityQuestions(response.data);
          } else {
            console.error('[LOCKS_SECURITY] Failed to fetch questions:', response.message);
            setLocksSecurityQuestions(LOCKS_SECURITY_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[LOCKS_SECURITY] Error fetching questions:', error);
          setLocksSecurityQuestions(LOCKS_SECURITY_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchLocksSecurityQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Plumbing questions from database
  useEffect(() => {
    if (step === 'plumbingQuestions' && formData.service_type) {
      const fetchPlumbingQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[PLUMBING] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[PLUMBING] Loaded ${response.data.length} questions from database`);
            setPlumbingQuestions(response.data);
          } else {
            console.error('[PLUMBING] Failed to fetch questions:', response.message);
            setPlumbingQuestions(PLUMBING_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[PLUMBING] Error fetching questions:', error);
          setPlumbingQuestions(PLUMBING_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchPlumbingQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Property Maintenance questions from database
  useEffect(() => {
    if (step === 'propertyMaintenanceQuestions' && formData.service_type) {
      const fetchPropertyMaintenanceQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[PROPERTY_MAINTENANCE] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[PROPERTY_MAINTENANCE] Loaded ${response.data.length} questions from database`);
            // API response is already transformed with correct format (label, type, required fields)
            setPropertyMaintenanceQuestions(response.data);
          } else {
            console.error('[PROPERTY_MAINTENANCE] Failed to fetch questions:', response.message);
            setPropertyMaintenanceQuestions(PROPERTY_MAINTENANCE_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[PROPERTY_MAINTENANCE] Error fetching questions:', error);
          setPropertyMaintenanceQuestions(PROPERTY_MAINTENANCE_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchPropertyMaintenanceQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Roofing questions from database
  useEffect(() => {
    if (step === 'roofingQuestions' && formData.service_type) {
      const fetchRoofingQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[ROOFING] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[ROOFING] Loaded ${response.data.length} questions from database`);
            // API response is already transformed with correct format (label, type, required fields)
            setRoofingQuestions(response.data);
          } else {
            console.error('[ROOFING] Failed to fetch questions:', response.message);
            setRoofingQuestions(ROOFING_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[ROOFING] Error fetching questions:', error);
          setRoofingQuestions(ROOFING_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchRoofingQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch questions for Windows, Doors & Glazing (and fallback to hardcoded for others)
  useEffect(() => {
    const isWindowsDoorsGlazingStep = step === 'windowsDoorsGlazingQuestions';
    
    if (isWindowsDoorsGlazingStep && formData.service_type) {
      const fetchWindowsDoorsQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[WINDOWS_DOORS_GLAZING] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[WINDOWS_DOORS_GLAZING] Loaded ${response.data.length} questions from database`);
            setDynamicQuestions(response.data);
          } else {
            console.error('[WINDOWS_DOORS_GLAZING] Failed to fetch questions:', response.message);
            setDynamicQuestions(WINDOWS_DOORS_GLAZING_DEFAULT_QUESTIONS);
          }
        } catch (error) {
          console.error('[WINDOWS_DOORS_GLAZING] Error fetching questions:', error);
          setDynamicQuestions(WINDOWS_DOORS_GLAZING_DEFAULT_QUESTIONS);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchWindowsDoorsQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Specialist Compliance questions from database
  useEffect(() => {
    if (step === 'specialistComplianceQuestions' && formData.service_type) {
      const fetchSpecialistComplianceQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[SPECIALIST_COMPLIANCE] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[SPECIALIST_COMPLIANCE] Loaded ${response.data.length} questions from database`);
            // API response is already transformed with correct format (label, type, required fields)
            setSpecialistComplianceQuestions(response.data);
          } else {
            console.error('[SPECIALIST_COMPLIANCE] Failed to fetch questions:', response.message);
            setSpecialistComplianceQuestions([]);
          }
        } catch (error) {
          console.error('[SPECIALIST_COMPLIANCE] Error fetching questions:', error);
          setSpecialistComplianceQuestions([]);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchSpecialistComplianceQuestions();
    }
  }, [step, formData.service_type]);

  // Fetch Waste & Clearance questions from database
  useEffect(() => {
    if (step === 'wasteClearanceQuestions' && formData.service_type) {
      const fetchWasteClearanceQuestions = async () => {
        try {
          setIsLoadingQuestions(true);
          console.log(`[WASTE_CLEARANCE] Fetching questions for service_type: ${formData.service_type}`);
          
          const response = await apiService.request(
            `/quote-form/service-questions/${formData.service_type}`,
            'GET'
          );
          
          if (response.success && response.data) {
            console.log(`[WASTE_CLEARANCE] Loaded ${response.data.length} questions from database`);
            // API response is already transformed with correct format (label, type, required fields)
            setWasteClearanceQuestions(response.data);
          } else {
            console.error('[WASTE_CLEARANCE] Failed to fetch questions:', response.message);
            setWasteClearanceQuestions([]);
          }
        } catch (error) {
          console.error('[WASTE_CLEARANCE] Error fetching questions:', error);
          setWasteClearanceQuestions([]);
        } finally {
          setIsLoadingQuestions(false);
        }
      };
      
      fetchWasteClearanceQuestions();
    }
  }, [step, formData.service_type]);

  const handleSubmit = async () => {
  if (!formData.service_category || !formData.service_type) {
    toast({ title: 'Error', description: 'Please select all required fields', variant: 'destructive' });
    return;
  }
  if (!formData.contact_name || !formData.contact_email || !formData.contact_phone || !formData.postcode) {
    toast({ title: 'Error', description: 'Please fill in all contact information including postcode', variant: 'destructive' });
    return;
  }

  setIsSubmitting(true);
  try {
    console.log('[FORM SUBMIT] Current formData.details BEFORE submission:', formData.details);
    console.log('[FORM SUBMIT] Frequency value:', formData.details.frequency, '| Breakouts:', formData.details.breakouts, '| Furniture:', formData.details.furniture_units);
    
    const submissionData: any = {
      service_category: Number(formData.service_category),
      service_type: formData.service_type,
      contact_name: formData.contact_name,
      contact_email: formData.contact_email,
      contact_phone: formData.contact_phone,
      postcode: formData.postcode,
      company: formData.company || '',
      property_type: formData.property_type || '',
      details: {}
    };

    // ============================================================
    // CORE CONSTRUCTION: Convert question codes (Q1, Q2...) to semantic field names
    // ============================================================
    if (Number(formData.service_category) === 15 && formData.property_type) {
      const propertyType = formData.property_type as keyof typeof CORE_CONSTRUCTION_FIELD_MAPPINGS;
      const fieldMapping = CORE_CONSTRUCTION_FIELD_MAPPINGS[propertyType];
      
      if (fieldMapping) {
        console.log(`[FORM SUBMIT] Converting Core Construction fields for ${propertyType}`);
        const convertedDetails: any = {};
        
        Object.keys(formData.details).forEach(questionCode => {
          // Check if this is a question code (Q1, Q2, etc)
          if (questionCode.match(/^Q\d+$/)) {
            const semanticFieldName = fieldMapping[questionCode];
            if (semanticFieldName) {
              convertedDetails[semanticFieldName] = formData.details[questionCode];
              console.log(`[FORM SUBMIT] Q code "${questionCode}" → "${semanticFieldName}"`);
            } else {
              // Keep original if no mapping found
              convertedDetails[questionCode] = formData.details[questionCode];
            }
          } else {
            // Keep non-question fields as-is (service_type, property_type)
            convertedDetails[questionCode] = formData.details[questionCode];
          }
        });
        
        submissionData.details = convertedDetails;
        console.log('[FORM SUBMIT] Converted details:', convertedDetails);
      }
    } else {
    
    // FIX: COPY ALL DETAILS FROM formData.details
    
    // Fields that MUST remain as strings
    const keepAsString = [
      'furniture_units',
      'frequency',
      'breakouts',
      'parking',
      'parking_type',
      'washrooms',
      'kitchens',
      'floor_levels',
      'urgency',
      'carpet_condition',
      'service_type',
      'property_type'
    ];
    
    // Copy all details from formData.details
    Object.keys(formData.details).forEach(key => {
      const value = formData.details[key];
      
      // Skip empty values
      if (value === '' || value === undefined || value === null) {
        if (key === 'frequency' || key === 'breakouts' || key === 'furniture_units') {
          console.log(`[FORM SUBMIT] SKIPPING (empty): ${key} = "${value}"`);
        }
        return;
      }
      
      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) {
        if (key === 'frequency' || key === 'breakouts' || key === 'furniture_units') {
          console.log(`[FORM SUBMIT] SKIPPING (empty array): ${key}`);
        }
        return;
      }
      
      // Handle based on type
      if (keepAsString.includes(key)) {
        submissionData.details[key] = String(value);
        console.log(`[FORM SUBMIT] Keeping as string: ${key} = ${String(value)}`);
      } else {
        submissionData.details[key] = value;
        console.log(`[FORM SUBMIT] Copying: ${key} = ${value}`);
      }
    });
    
    } // End of else block for non-Core Construction categories
    
    // ============================================================
    // FREQUENCY FIELD: Ensure clean ID without multiplier suffix
    // ============================================================
    if (formData.details.frequency && Number(formData.service_category) !== 15) {
      // Extract just the option ID (remove any multiplier suffix like "(1.00)")
      let frequencyValue = String(formData.details.frequency).trim();
      
      // If it contains parentheses with multiplier, extract just the ID part
      if (frequencyValue.includes('(')) {
        frequencyValue = frequencyValue.split('(')[0].trim();
      }
      
      submissionData.details.frequency = frequencyValue;
      console.log('[FORM SUBMIT] Frequency field - Original:', formData.details.frequency, '| Clean:', frequencyValue);
    }
    
    // ============================================================
    // BREAKOUTS FIELD: Ensure clean ID (non-Core Construction only)
    // ============================================================
    if (formData.details.breakouts && Number(formData.service_category) !== 15) {
      let breakoutsValue = String(formData.details.breakouts).trim();
      if (breakoutsValue.includes('(')) {
        breakoutsValue = breakoutsValue.split('(')[0].trim();
      }
      submissionData.details.breakouts = breakoutsValue;
      console.log('[FORM SUBMIT] Breakouts field - Original:', formData.details.breakouts, '| Clean:', breakoutsValue);
    }
    
    // ============================================================
    // FURNITURE_UNITS FIELD: Ensure clean ID (non-Core Construction only)
    // ============================================================
    if (formData.details.furniture_units && Number(formData.service_category) !== 15) {
      let furnitureValue = String(formData.details.furniture_units).trim();
      if (furnitureValue.includes('(')) {
        furnitureValue = furnitureValue.split('(')[0].trim();
      }
      submissionData.details.furniture_units = furnitureValue;
      console.log('[FORM SUBMIT] Furniture units field - Original:', formData.details.furniture_units, '| Clean:', furnitureValue);
    }
    
    // ============================================================
    // Handle Airbnb service type
    // ============================================================
    if (formData.airbnb_service_type) {
      submissionData.airbnb_service_type = Number(formData.airbnb_service_type);
      submissionData.details.airbnb_service_type = Number(formData.airbnb_service_type);
    }
    
    // ============================================================
    // FIELD RENAME OPERATIONS
    // ============================================================
    if (submissionData.details.parking !== undefined) {
      submissionData.details.parking_type = submissionData.details.parking;
      delete submissionData.details.parking;
    }
    
    if (submissionData.details.bathrooms_toilets !== undefined) {
      submissionData.details.bathrooms = submissionData.details.bathrooms_toilets;
      delete submissionData.details.bathrooms_toilets;
    }
    
    // ============================================================
    // Details object now contains only question responses, not metadata
    // (service_type and property_type remain at top level only)
    // ============================================================
    console.log('[FORM SUBMIT] Final submission data:', {
      service_type: submissionData.service_type,
      property_type: submissionData.property_type,
      details_keys: Object.keys(submissionData.details),
      details_sample: { 
        floor_area: submissionData.details.floor_area,
        frequency: submissionData.details.frequency,
        breakouts: submissionData.details.breakouts,
        furniture_units: submissionData.details.furniture_units
      }
    });

    if (submissionData.details.frequency) {
      console.log(`✅ [COMMERCIAL OFFICE] SUBMITTING WITH FREQUENCY: "${submissionData.details.frequency}"`);
    } else {
      console.log(`❌ [COMMERCIAL OFFICE] WARNING: NO FREQUENCY VALUE! Will default to "Weekly" on backend`);
    }
    
    // Determine endpoint
    let endpoint = '/service-requests';
    if (formData.property_type === 'event') {
      endpoint = API_CONFIG.ENDPOINTS.EVENT_VENUE_QUOTES;
    } else if (formData.property_type === 'hmo') {
      endpoint = API_CONFIG.ENDPOINTS.HMO_QUOTES;
    } else if (formData.property_type === 'specialist') {
      endpoint = API_CONFIG.ENDPOINTS.SPECIALIST_CLEANING_QUOTES;
    }
    
    const response = await apiService.request(endpoint, 'POST', submissionData);
    
    if (response.success) {
      setSubmissionData(response);
      setIsSubmitted(true);
      toast({ title: 'Success!', description: 'Your service request has been submitted successfully' });
    } else {
      toast({ title: 'Error', description: response.message || 'Failed to submit', variant: 'destructive' });
    }
  } catch (error: any) {
    console.error('[FORM SUBMIT] Error:', error);
    toast({ title: 'Error', description: error.message || 'Failed to submit', variant: 'destructive' });
  } finally {
    setIsSubmitting(false);
  }
};

  // Render functions for each step
  const renderCategoryStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Select Service Category</h2>
      <div className="grid grid-cols-3 gap-4">
        {serviceCategories.length > 0 ? (
          serviceCategories.map(cat => {
            return (
            <button
              key={cat.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Set step FIRST before updating category to avoid useEffect race conditions
                
                // Set step FIRST before updating category to avoid useEffect race conditions
                if (Number(cat.id) === 1 || Number(cat.id) === 6 || cat.label?.toLowerCase().includes('cleaning')) {
                  setStep('cleaningCategoryStep');
                } else if (Number(cat.id) === 14) {
                  setStep('carpentryServiceType');
                } else if (Number(cat.id) === 15) {
                  setStep('coreConstructionServiceType');
                } else if (Number(cat.id) === 17) {
                  setStep('electricalServiceType');
                } else if (Number(cat.id) === 16 || cat.label?.toLowerCase().includes('external works') || cat.label?.toLowerCase().includes('landscaping')) {
                  setStep('landscapingServiceType');
                } else if (Number(cat.id) === 18 || cat.label?.toLowerCase().includes('finishing') || cat.label?.toLowerCase().includes('interiors')) {
                  setStep('finishingInteriorsServiceType');
                } else if (Number(cat.id) === 19 || cat.label?.toLowerCase().includes('kitchens') || cat.label?.toLowerCase().includes('bathrooms')) {
                  setStep('kitchensBathroomsServiceType');
                } else if (Number(cat.id) === 20 || cat.label?.toLowerCase().includes('locks') || cat.label?.toLowerCase().includes('security')) {
                  setStep('locksSecurityServiceType');
                } else if (Number(cat.id) === 21 || cat.label?.toLowerCase().includes('plumbing')) {
                  setStep('plumbingServiceType');
                } else if (Number(cat.id) === 22 || cat.label?.toLowerCase().includes('property maintenance')) {
                  setStep('propertyMaintenanceServiceType');
                } else if (Number(cat.id) === 23 || cat.label?.toLowerCase().includes('roofing')) {
                  setStep('roofingServiceType');
                } else if (Number(cat.id) === 24 || cat.label?.toLowerCase().includes('specialist') || cat.label?.toLowerCase().includes('compliance')) {
                  setStep('specialistComplianceServiceType');
                } else if (Number(cat.id) === 25 || cat.label?.toLowerCase().includes('waste') || cat.label?.toLowerCase().includes('clearance')) {
                  setStep('wasteClearanceServiceType');
                } else if (Number(cat.id) === 26 || cat.label?.toLowerCase().includes('windows') || cat.label?.toLowerCase().includes('doors') || cat.label?.toLowerCase().includes('glazing')) {
                  setStep('windowsDoorsGlazingServiceType');
                } else {
                  setStep('serviceType');
                }
                // Update category AFTER setting step, only update service_category
                // Clear property_type so Cleaning can re-ask for it
                setFormData(prev => ({
                  ...prev,
                  service_category: String(cat.id),
                  property_type: ''
                }));
              }}
              className={`p-6 rounded-lg border-2 transition text-center ${
                Number(formData.service_category) === Number(cat.id)
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="text-4xl mb-3">{cat.icon || '📦'}</div>
              <div className="font-semibold text-gray-900 text-sm">{cat.label}</div>
            </button>
          );
          })
        ) : (
          <p className="col-span-3 text-gray-500 text-center">No service categories available</p>
        )}
      </div>
    </div>
  );

  const renderServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Select Service Type</h2>
      <div className="grid grid-cols-3 gap-4">
        {serviceTypes.length > 0 ? (
          serviceTypes.map(svc => {
            // Special handling for Carpentry category (ID 14)
            const isCarpentryCategory = Number(formData.service_category) === 14;
            // For categories with dedicated service type steps (14-18), just use category name as property_id
            // Don't use the service UUID as property_id, that causes incorrect form state
            const categoryId = Number(formData.service_category);
            const categoryBasedPropertyId = categoryId === 14 ? 'carpentry' :
                                           categoryId === 15 ? 'core_construction' :
                                           categoryId === 16 ? 'landscaping' :
                                           categoryId === 17 ? 'electrical' :
                                           categoryId === 18 ? 'finishing_interiors' : null;
            const propertyId = categoryBasedPropertyId || svc.property_id || String(svc.id);
            const isSelected = formData.service_type === String(svc.id) || formData.service_type === svc.id;
            
            return (
              <button
                key={svc.id}
                onClick={() => {
                  updateField('property_type', propertyId);
                  updateField('service_type', String(svc.id));
                  
                  const isCleaningCategory = Number(formData.service_category) === 6;
                  const isCoreConstructionCategory = Number(formData.service_category) === 15;
                  const isLocksSecurityCategory = Number(formData.service_category) === 20;
                  const isPlumbingCategory = Number(formData.service_category) === 21;
                  const isPropertyMaintenanceCategory = Number(formData.service_category) === 22;
                  const isRoofingCategory = Number(formData.service_category) === 23;
                  const isAirbnbProperty = propertyId === 'airbnb';
                  const isCommercialProperty = propertyId === 'commercial';
                  const isEventProperty = propertyId === 'event';
                  const isHmoProperty = propertyId === 'hmo';
                  const isSpecialistProperty = propertyId === 'specialist';
                  const isCarpentryProperty = propertyId === 'carpentry';
                  
                  // For new service categories with dedicated service type selectors,
                  // route directly to questions (skip intermediate step)
                  const isElectricalCategory = Number(formData.service_category) === 17;
                  const isLandscapingCategory = Number(formData.service_category) === 16;
                  const isFinishingInteriorsCategory = Number(formData.service_category) === 18;

                  if (isCoreConstructionCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to coreConstructionQuestions`);
                    setStep('coreConstructionQuestions');
                  } else if (isElectricalCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to electricalQuestions`);
                    setStep('electricalQuestions');
                  } else if (isLandscapingCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to landscapingQuestions`);
                    setStep('landscapingQuestions');
                  } else if (isFinishingInteriorsCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to finishingInteriorsQuestions`);
                    setStep('finishingInteriorsQuestions');
                  } else if (isLocksSecurityCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to locksSecurityQuestions`);
                    setStep('locksSecurityQuestions');
                  } else if (isPlumbingCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to plumbingQuestions`);
                    setStep('plumbingQuestions');
                  } else if (isPropertyMaintenanceCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to propertyMaintenanceQuestions`);
                    setStep('propertyMaintenanceQuestions');
                  } else if (isRoofingCategory) {
                    console.log(`[SERVICE-TYPE-SELECT] Routing directly to roofingQuestions`);
                    setStep('roofingQuestions');
                  } else if (isCleaningCategory && isAirbnbProperty) {
                    setStep('airbnbService');
                  } else if (isCleaningCategory && isCommercialProperty) {
                    setStep('commercialServiceType');
                  } else if (isCleaningCategory && isEventProperty) {
                    setStep('eventVenueServiceType');
                  } else if (isCleaningCategory && isHmoProperty) {
                    setStep('hmoServiceType');
                  } else if (isCleaningCategory && isSpecialistProperty) {
                    setStep('specialistCleaningServiceType');
                  } else if (isCleaningCategory) {
                    setStep('cleaningServiceType');
                  } else if (isCarpentryProperty) {
                    setStep('carpentryServiceType');
                  } else {
                    setStep('contact');
                  }
                }}
                className={`p-6 rounded-lg border-2 transition text-center ${
                  isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <div className="text-4xl mb-3">{svc.icon || '🏠'}</div>
                <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
                {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
              </button>
            );
          })
        ) : (
          <p className="col-span-3 text-gray-500 text-center">No service types available for this selection</p>
        )}
      </div>
    </div>
  );

  const renderAirbnbServiceStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Select Cleaning Service Type</h2>
      <p className="text-gray-600">Choose the service that best matches your turnaround needs</p>
      <div className="grid grid-cols-2 gap-4">
        {AIRBNB_SERVICE_TYPES.map(airbnb => (
          <button
            key={airbnb.id}
            onClick={() => {
              updateField('airbnb_service_type', String(airbnb.id));
              setStep(airbnb.id === 6 ? 'hotelHousekeepingMessage' : 'airbnbDetails');
            }}
            className={`p-4 rounded-lg border-2 transition text-left ${
              Number(formData.airbnb_service_type) === airbnb.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'
            }`}
          >
            <div className="text-3xl mb-2">{airbnb.icon}</div>
            <div className="font-semibold text-gray-900 text-sm mb-1">{airbnb.label}</div>
            <div className="text-xs text-gray-600">{airbnb.description}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderCommercialServiceTypeStep = () => (
    <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Commercial Office Service</h2>
        <p className="text-gray-600">Select the cleaning service that best fits your needs</p>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {isLoadingServiceTypes ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading service types...</p>
          </div>
        ) : serviceTypes.length > 0 ? (
          serviceTypes.map(svc => (
            <button
              key={svc.id}
              onClick={() => {
                updateField('service_type', String(svc.id || svc.service_type_id));
                console.log(`[COMMERCIAL-SERVICE-SELECT] property_type: ${formData.property_type}, service_type: ${svc.id || svc.service_type_id}, name: ${svc.label || svc.name}`);
                setStep('questions');
              }}
              className="p-5 rounded-lg border-2 transition text-left hover:shadow-md border-gray-300 bg-white hover:border-blue-400"
            >
              <div className="font-semibold text-gray-900 text-lg">{svc.label || svc.name}</div>
              {svc.description && <div className="text-sm text-gray-600 mt-2">{svc.description}</div>}
            </button>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-2">No service types available for this property.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderEventVenueServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Select Event Venue Service Type</h2>
      <p className="text-gray-600">Choose which service you need for your event</p>
      <div className="grid grid-cols-1 gap-4">
        {EVENT_VENUE_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              updateField('service_type', String(svc.id));
              setStep('questions');
            }}
            className="p-5 rounded-lg border-2 transition text-left hover:shadow-md border-gray-300 bg-white hover:border-blue-400"
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-lg">{svc.label}</div>
            <div className="text-sm text-gray-600 mt-2">{svc.description}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderHmoServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Select HMO Service Type</h2>
      <p className="text-gray-600">Choose the cleaning service for your HMO property</p>
      <div className="grid grid-cols-1 gap-4">
        {HMO_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              updateField('service_type', String(svc.id));
              setStep('questions');
            }}
            className="p-5 rounded-lg border-2 transition text-left hover:shadow-md border-gray-300 bg-white hover:border-blue-400"
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-lg">{svc.label}</div>
            <div className="text-sm text-gray-600 mt-2">{svc.description}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderSpecialistCleaningServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Specialist Cleaning Service</h2>
      <p className="text-gray-600">Select your specialist cleaning requirement</p>
      <div className="grid grid-cols-1 gap-4">
        {SPECIALIST_CLEANING_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              updateField('service_type', String(svc.id));
              setStep('specialistUnavailable');
            }}
            className={`p-5 rounded-lg border-2 transition text-left hover:shadow-md ${
              formData.service_type === String(svc.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-white hover:border-blue-400'
            }`}
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-lg">{svc.label}</div>
            <div className="text-sm text-gray-600 mt-2">{svc.description}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderCleaningServiceCategoryStep = () => {
    // For Category 1 (Cleaning) - show proper cleaning services with mapping
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Cleaning & Care Services</h2>
        <p className="text-gray-600">Select the specific cleaning service you need</p>
        <div className="grid grid-cols-2 gap-4">
          {[
            { id: 1, label: 'Regular Domestic Cleaning', icon: '🧹', service_type: 'regular-domestic-cleaning' },
            { id: 2, label: 'Deep Cleaning', icon: '✨', service_type: 'deep-cleaning' },
            { id: 3, label: 'After-Builders Cleaning', icon: '🏗️', service_type: 'after-builders-residential' },
            { id: 4, label: 'Commercial Carpet Cleaning', icon: '🧵', service_type: 'commercial-carpet-cleaning' },
            { id: 5, label: 'Event Cleaning', icon: '🎉', service_type: 'event-cleaning' },
            { id: 6, label: 'Specialist Cleaning', icon: '🔬', service_type: 'specialist-cleaning' },
            { id: 7, label: 'Core Contract Cleaning', icon: '🏢', service_type: 'core-contract-cleaning' },
            { id: 8, label: 'End of Tenancy Cleaning', icon: '🏠', service_type: 'eot-cleaning' }
          ].map(svc => (
            <button
              key={svc.id}
              onClick={() => {
                console.log(`[CLEANING-CATEGORY-CLICK] Selected: ${svc.label} (${svc.service_type})`);
                setFormData(prev => ({
                  ...prev,
                  service_category: 1,
                  service_type: svc.service_type,
                  property_type: 'residential'
                }));
                // For Category 1 Cleaning, go to questions
                setTimeout(() => {
                  console.log(`[CLEANING-CATEGORY-CLICK] Moving to questions (Category 1)`);
                  setStep('questions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === svc.service_type
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderCleaningServiceTypeStep = () => {
    // If property_type is not set, show property type selector first
    if (!formData.property_type) {
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">What type of property is this?</h2>
          <p className="text-gray-600">Select the property type for your cleaning service</p>
          <div className="grid grid-cols-2 gap-4">
            {DEFAULT_PROPERTY_TYPES.map(prop => (
              <button
                key={prop.id}
                onClick={() => {
                  console.log(`[CLEANING] Property type selected: ${prop.property_id}`);
                  updateField('property_type', prop.property_id);
                }}
                className="p-4 rounded-lg border-2 transition text-left border-gray-300 bg-white hover:border-blue-400"
              >
                <div className="text-3xl mb-2">{prop.icon}</div>
                <div className="font-semibold text-gray-900 text-sm">{prop.label}</div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Property type is set, show cleaning service types
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Select Cleaning Service Type</h2>
        <p className="text-gray-600">Choose the specific cleaning service you need</p>
        <div className="grid grid-cols-2 gap-4">
          {isLoadingServiceTypes ? (
            <div className="col-span-2 text-center py-8">
              <p className="text-gray-500">Loading service types...</p>
            </div>
          ) : serviceTypes.length > 0 ? (
            serviceTypes.map(svc => (
              <button
                key={svc.id}
                onClick={() => {
                  console.log('Clicked:', svc.id, svc.label);
                  updateField('service_type', String(svc.id || svc.service_type_id));

                  // Check if this is specialist cleaning
                  if (svc.id === 'specialist' || svc.property_id === 'specialist') {
                    console.log('Specialist selected - changing to specialistUnavailable');
                    updateField('property_type', 'specialist');
                    setStep('specialistUnavailable');
                  } else {
                    console.log('Regular service selected - going to questions');
                    // Set default property_type for cleaning if not already set
                    if (!formData.property_type) {
                      updateField('property_type', 'residential');
                      console.log('Set default property_type to residential');
                    }
                    setStep('questions');
                  }
                }}
                className="p-4 rounded-lg border-2 transition text-left border-gray-300 bg-white hover:border-blue-400"
              >
                <div className="text-3xl mb-2">🧹</div>
                <div className="font-semibold text-gray-900 text-sm">{svc.label || svc.name}</div>
                {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
              </button>
            ))
          ) : (
            <div className="col-span-2 text-center py-8">
              <p className="text-gray-500 mb-2">No service types available for this property.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCarpentryServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Carpentry & Joinery Services</h2>
      <p className="text-gray-600">Select the specific carpentry service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {CARPENTRY_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              console.log(`[CARPENTRY CLICK] Selected service: ID=${svc.id}, label=${svc.label}`);
              // Update both service_type AND property_type before moving to questions
              setFormData(prev => ({
                ...prev,
                property_type: 'carpentry',
                service_type: String(svc.id)
              }));
              // Small delay to ensure state is updated before step change
              setTimeout(() => {
                console.log(`[CARPENTRY CLICK] Moving to questions step`);
                setStep('questions');
              }, 0);
            }}
            className={`p-4 rounded-lg border-2 transition text-left ${
              formData.service_type === String(svc.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-white hover:border-blue-400'
            }`}
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
            {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
          </button>
        ))}
      </div>
    </div>
  );

  const renderCoreConstructionServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Core Construction & Structural Services</h2>
      <p className="text-gray-600">Select the specific construction service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {CORE_CONSTRUCTION_SERVICE_TYPES.map(svc => {
          const serviceTypeId = CORE_CONSTRUCTION_SERVICE_TYPE_MAPPING[svc.id as keyof typeof CORE_CONSTRUCTION_SERVICE_TYPE_MAPPING] || 'core-construction';
          return (
            <button
              key={svc.id}
              onClick={() => {
                console.log(`[CORE-CONSTRUCTION CLICK] Selected service: ID=${svc.id}, label=${svc.label}, mapped_type=${serviceTypeId}`);
                setFormData(prev => ({
                  ...prev,
                  service_category: 15,
                  service_type: serviceTypeId
                }));
                setTimeout(() => {
                  console.log(`[CORE-CONSTRUCTION CLICK] Moving to coreConstructionQuestions step`);
                  setStep('coreConstructionQuestions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === serviceTypeId
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderElectricalServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Electrical & Smart Systems Services</h2>
      <p className="text-gray-600">Select the specific electrical service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {ELECTRICAL_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                property_type: svc.property_id,
                service_type: String(svc.id)
              }));
              setTimeout(() => {
                setStep('electricalQuestions');
              }, 0);
            }}
            className={`p-4 rounded-lg border-2 transition text-left ${
              formData.service_type === String(svc.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-white hover:border-blue-400'
            }`}
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
            {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
          </button>
        ))}
      </div>
    </div>
  );

  const renderLandscapingServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">External Works & Landscaping Services</h2>
      <p className="text-gray-600">Select the specific landscaping service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {LANDSCAPING_SERVICE_TYPES.map(svc => {
          const serviceTypeId = EXTERNAL_WORKS_SERVICE_TYPE_MAPPING[svc.id as keyof typeof EXTERNAL_WORKS_SERVICE_TYPE_MAPPING] || 'specialist-cleaning';
          return (
            <button
              key={svc.id}
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  service_category: 16,
                  service_type: serviceTypeId
                }));
                setTimeout(() => {
                  setStep('landscapingQuestions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === serviceTypeId
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 bg-white hover:border-green-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderFinishingInteriorsServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Finishing & Interiors Services</h2>
      <p className="text-gray-600">Select the specific finishing & interiors service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {FINISHING_INTERIORS_SERVICE_TYPES.map(svc => {
          const serviceTypeId = FINISHING_INTERIORS_SERVICE_TYPE_MAPPING[svc.id as keyof typeof FINISHING_INTERIORS_SERVICE_TYPE_MAPPING] || 'after-builders-residential';
          return (
            <button
              key={svc.id}
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  service_category: 18,
                  service_type: serviceTypeId
                }));
                setTimeout(() => {
                  setStep('finishingInteriorsQuestions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === serviceTypeId
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-300 bg-white hover:border-purple-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderKitchensBathroomsServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Kitchens & Bathrooms Services</h2>
      <p className="text-gray-600">Select the specific kitchens & bathrooms service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {KITCHENS_BATHROOMS_SERVICE_TYPES.map(svc => {
          const serviceTypeId = KITCHENS_BATHROOMS_SERVICE_TYPE_MAPPING[svc.id as keyof typeof KITCHENS_BATHROOMS_SERVICE_TYPE_MAPPING] || 'kitchen-refurb';
          return (
            <button
              key={svc.id}
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  service_category: 19,
                  service_type: serviceTypeId
                }));
                setTimeout(() => {
                  setStep('kitchensBathroomsQuestions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === serviceTypeId
                  ? 'border-cyan-500 bg-cyan-50'
                  : 'border-gray-300 bg-white hover:border-cyan-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderLocksSecurityServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Locks & Security Services</h2>
      <p className="text-gray-600">Select the specific locks & security service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {LOCKS_SECURITY_SERVICE_TYPES.map(svc => {
          const serviceTypeId = LOCKS_SECURITY_SERVICE_TYPE_MAPPING[svc.id as keyof typeof LOCKS_SECURITY_SERVICE_TYPE_MAPPING] || 'fire-alarm-systems';
          return (
            <button
              key={svc.id}
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  service_category: 20,
                  service_type: serviceTypeId
                }));
                setTimeout(() => {
                  setStep('locksSecurityQuestions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === serviceTypeId
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 bg-white hover:border-red-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Mapping of plumbing service IDs to their service_type_ids
  const PLUMBING_SERVICE_TYPE_MAPPING = {
    500: 'plumbing-bathroom',
    501: 'plumbing-boiler-installation',
    502: 'plumbing-central-heating',
    503: 'plumbing-emergency',
    504: 'plumbing-gas-engineering',
    505: 'plumbing-general',
    506: 'plumbing-hot-water-cylinders',
    507: 'plumbing-kitchen',
    508: 'plumbing-leak-detection',
    509: 'plumbing-pipework',
    510: 'plumbing-radiator-installation',
    511: 'plumbing-underfloor-heating',
    512: 'plumbing-unvented-systems'
  };

  // Mapping of Core Construction service IDs to service_type_ids
  const CORE_CONSTRUCTION_SERVICE_TYPE_MAPPING = {
    126: 'core_const_blockwork',        // Blockwork
    127: 'core_const_bricklaying',      // Bricklaying
    128: 'core_const_concrete',         // Concrete Works
    129: 'core_const_foundations',      // Foundations & Footings
    130: 'core_const_groundworks',      // Groundworks
    131: 'core_const_steel',            // Steel Installation
    132: 'core_const_repairs',          // Structural Repairs
    133: 'core_const_underpinning'      // Underpinning
  };

  // Mapping of Locks & Security service IDs to service_type_ids
  const LOCKS_SECURITY_SERVICE_TYPE_MAPPING = {
    400: 'intruder-alarms',      // Lock Changes → Alarm systems (closest match)
    401: 'fire-alarm-systems'    // Locksmith Services → Fire alarm systems
  };

  // Mapping of Kitchens & Bathrooms service IDs to service_type_ids
  const KITCHENS_BATHROOMS_SERVICE_TYPE_MAPPING = {
    300: 'bathroom-refurb',      // Bathroom Installation
    301: 'bathroom-refurb',      // Bathroom Refurbishment
    302: 'kitchen-refurb',       // Full Kitchen Installation
    303: 'kitchen-refurb',       // Kitchen Refurbishment
    304: 'bathroom-refurb',      // Vanity & Sanitaryware
    305: 'bathroom-refurb',      // Washrooms
    306: 'bathroom-refurb'       // Wet Rooms
  };

  // Mapping of Finishing & Interiors service IDs to service_type_ids
  const FINISHING_INTERIORS_SERVICE_TYPE_MAPPING = {
    200: 'plastering-finishing',      // Ceiling Installation → Plastering
    201: 'plastering-finishing',      // Drylining → Plastering
    202: 'painting-decorating',       // Feature Walls → Painting
    203: 'flooring-finishing',        // Flooring → Flooring
    204: 'painting-decorating',       // Painting & Decorating
    205: 'plastering-finishing',      // Plastering
    206: 'rendering-external',        // Rendering
    207: 'tiling-finishing',          // Tiling
    208: 'wallpapering-finishing'     // Wallpapering
  };

  // Mapping of External Works & Landscaping service IDs to service_type_ids
  const EXTERNAL_WORKS_SERVICE_TYPE_MAPPING = {
    170: 'decking-installation',
    171: 'fencing-services',
    172: 'garden-clearance',
    173: 'garden-maintenance',
    174: 'hedge-cutting',
    175: 'landscaping-design',
    176: 'pressure-washing',
    177: 'tree-surgery',
    178: 'turfing-services'
  };

  // Mapping of Cleaning & Care service IDs to service_type_ids
  // Category 1: Cleaning & Care services
  const CLEANING_SERVICE_TYPE_MAPPING = {
    1: 'regular-domestic-cleaning',        // Domestic Cleaning
    2: 'deep-cleaning',                    // Deep Cleaning
    3: 'after-builders-residential',       // After-Builders Cleaning
    4: 'commercial-carpet-cleaning',       // Commercial Cleaning
    5: 'event-cleaning',                   // Event Cleaning
    6: 'specialist-cleaning',              // Specialist Cleaning
    7: 'core-contract-cleaning',           // Core Contract Cleaning
    8: 'eot-cleaning'                      // End of Tenancy Cleaning
  };

  // Mapping of Windows/Doors/Glazing service UUIDs to their service_type_ids
  // Most services use generic 'windows-doors-glazing' questions
  // Only shopfront-glazing has specialized questions
  const WINDOWS_DOORS_GLAZING_SERVICE_MAPPING: Record<string, string> = {
    '3b1950df-465f-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Window Installation
    '3b196956-465f-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Door Installation
    '3b196b75-465f-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Glass Repair
    '3b196cc2-465f-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Frame Repair
    '3b17fdd0-465f-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Tile Repair
    '3b17ffbf-465f-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Chimney Repair
    'a217e3ec-4606-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Window Cleaning
    'a217f60c-4606-11f1-a72e-5405dbd9ade4': 'windows-doors-glazing',  // Gutter Cleaning
    'shopfront-glazing': 'shopfront-glazing'  // Special: Shopfront glazing with custom questions
  };

  const renderPlumbingServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Plumbing, Heating & Gas Services</h2>
      <p className="text-gray-600">Select the specific plumbing service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {PLUMBING_SERVICE_TYPES.map(svc => {
          const serviceTypeId = PLUMBING_SERVICE_TYPE_MAPPING[svc.id as keyof typeof PLUMBING_SERVICE_TYPE_MAPPING] || 'plumbing-general';
          return (
            <button
              key={svc.id}
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  property_type: svc.property_id,
                  service_category: 21,
                  service_type: serviceTypeId
                }));
                setTimeout(() => {
                  setStep('plumbingQuestions');
                }, 0);
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === serviceTypeId
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderPropertyMaintenanceServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Property Maintenance & Handyman Services</h2>
      <p className="text-gray-600">Select the specific property maintenance service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {PROPERTY_MAINTENANCE_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                service_category: 22,
                property_type: svc.property_id,
                service_type: 'property-maintenance'
              }));
              setTimeout(() => {
                setStep('propertyMaintenanceQuestions');
              }, 0);
            }}
            className={`p-4 rounded-lg border-2 transition text-left ${
              formData.service_type === 'property-maintenance'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-gray-300 bg-white hover:border-yellow-400'
            }`}
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
            {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
          </button>
        ))}
      </div>
    </div>
  );

  const renderRoofingServiceTypeStep = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Roofing Services</h2>
      <p className="text-gray-600">Select the specific roofing service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {ROOFING_SERVICE_TYPES.map(svc => (
          <button
            key={svc.id}
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                property_type: svc.property_id,
                service_type: 'roofing'
              }));
              setTimeout(() => {
                setStep('roofingQuestions');
              }, 0);
            }}
            className={`p-4 rounded-lg border-2 transition text-left ${
              formData.service_type === 'roofing'
                ? 'border-slate-500 bg-slate-50'
                : 'border-gray-300 bg-white hover:border-slate-400'
            }`}
          >
            <div className="text-3xl mb-2">{svc.icon}</div>
            <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
            {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
          </button>
        ))}
      </div>
    </div>
  );

  // ========== NEW CATEGORY RENDER FUNCTIONS (IDs 24, 25, 26) ==========

  const renderSpecialistComplianceServiceType = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Specialist & Compliance Services</h2>
      <p className="text-gray-600">Select the specific specialist or compliance service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {serviceTypes.length > 0 ? (
          serviceTypes.map(svc => (
            <button
              key={svc.id}
              onClick={() => {
                updateField('service_type', 'specialist-compliance');
                console.log(`[SPECIALIST-COMPLIANCE] Selected: ${svc.label}`);
                setStep('specialistComplianceQuestions');
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === 'specialist-compliance'
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 bg-white hover:border-red-400'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon || '⚠️'}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          ))
        ) : (
          <p className="col-span-2 text-gray-500 text-center">Loading services...</p>
        )}
      </div>
    </div>
  );

  const renderWasteClearanceServiceType = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Waste & Clearance Services</h2>
      <p className="text-gray-600">Select the specific waste or clearance service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {serviceTypes.length > 0 ? (
          serviceTypes.map(svc => (
            <button
              key={svc.id}
              onClick={() => {
                updateField('service_type', 'waste-clearance');
                console.log(`[WASTE-CLEARANCE] Selected: ${svc.label}`);
                setStep('wasteClearanceQuestions');
              }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                formData.service_type === 'waste-clearance'
                  ? 'border-amber-700 bg-amber-50'
                  : 'border-gray-300 bg-white hover:border-amber-600'
              }`}
            >
              <div className="text-3xl mb-2">{svc.icon || '🗑️'}</div>
              <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
              {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
            </button>
          ))
        ) : (
          <p className="col-span-2 text-gray-500 text-center">Loading services...</p>
        )}
      </div>
    </div>
  );

  const renderWindowsDoorsGlazingServiceType = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Windows, Doors & Glazing Services</h2>
      <p className="text-gray-600">Select the specific window, door, or glazing service you need</p>
      <div className="grid grid-cols-2 gap-4">
        {serviceTypes.length > 0 ? (
          serviceTypes.map(svc => {
            const serviceTypeId = WINDOWS_DOORS_GLAZING_SERVICE_MAPPING[svc.id as keyof typeof WINDOWS_DOORS_GLAZING_SERVICE_MAPPING] || 'windows-doors-glazing';
            return (
              <button
                key={svc.id}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    service_category: 26,
                    service_type: serviceTypeId
                  }));
                  setTimeout(() => {
                    setStep('windowsDoorsGlazingQuestions');
                  }, 0);
                }}
                className={`p-4 rounded-lg border-2 transition text-left ${
                  formData.service_type === serviceTypeId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-blue-400'
                }`}
              >
                <div className="text-3xl mb-2">{svc.icon || '🪟'}</div>
                <div className="font-semibold text-gray-900 text-sm">{svc.label}</div>
                {svc.description && <div className="text-xs text-gray-600 mt-1">{svc.description}</div>}
              </button>
            );
          })
        ) : (
          <p className="col-span-2 text-gray-500 text-center">Loading services...</p>
        )}
        {/* Special case: Add Shopfront Glazing option */}
        <button
          onClick={() => {
            setFormData(prev => ({
              ...prev,
              service_category: 26,
              service_type: 'shopfront-glazing'
            }));
            setTimeout(() => {
              setStep('windowsDoorsGlazingQuestions');
            }, 0);
          }}
          className={`p-4 rounded-lg border-2 transition text-left ${
            formData.service_type === 'shopfront-glazing'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-white hover:border-blue-400'
          }`}
        >
          <div className="text-3xl mb-2">🏪</div>
          <div className="font-semibold text-gray-900 text-sm">Shopfront Glazing</div>
          <div className="text-xs text-gray-600 mt-1">Commercial shopfront glazing</div>
        </button>
      </div>
    </div>
  );

  const renderHotelHousekeepingMessageStep = () => (
    <div className="space-y-6 text-center py-12">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-8">
        <h2 className="text-3xl font-bold text-amber-900 mb-4">Instant Quote Service Not Available For This Service In Your Area Just Yet…</h2>
        <p className="text-lg text-amber-800 mb-6">Contact Us Directly As We May Still Be Able To Provide You A Quote!</p>
        <div className="flex flex-col gap-3 items-center">
          <a href="tel:+44" className="text-blue-600 hover:text-blue-800 font-semibold text-lg">📞 Call Us</a>
          <a href="mailto:info@virewire.com" className="text-blue-600 hover:text-blue-800 font-semibold text-lg">📧 Email Us</a>
        </div>
      </div>
    </div>
  );

  const renderSpecialistUnavailableStep = () => (
    <div className="space-y-6 text-center py-12">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-8">
        <h2 className="text-3xl font-bold text-amber-900 mb-4">Instant Quote Service Not Available For This Service In Your Area Just Yet…</h2>
        <p className="text-lg text-amber-800 mb-6">Contact Us Directly As We May Still Be Able To Provide You A Quote!</p>
        <div className="flex flex-col gap-3 items-center">
          <a href="tel:+44" className="text-blue-600 hover:text-blue-800 font-semibold text-lg">📞 Call Us</a>
          <a href="mailto:info@virewire.com" className="text-blue-600 hover:text-blue-800 font-semibold text-lg">📧 Email Us</a>
        </div>
      </div>
    </div>
  );

  const renderAirbnbDetailsStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Property Details</h2>
        <p className="text-gray-600">Please provide information about your property</p>
      </div>
      {AIRBNB_DETAILS_QUESTIONS.map((q, qIdx) => (
        <div key={`aq_${qIdx}_${q.id}`} className="mb-6">
          <Label className="font-semibold text-gray-900">{q.label} {q.required && <span className="text-red-500 ml-1">*</span>}</Label>
          {q.type === 'select' && Array.isArray(q.options) && (
            <select
              value={formData.details[q.id] || ''}
              onChange={e => {
                const selectedValue = e.target.value;
                // Find the selected option by its value
                const selectedOption = q.options.find((opt: any) => {
                  const optValue = (opt as any)?.value || (opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String((opt as any)?.id);
                  return optValue === selectedValue;
                });
                // Store the option VALUE (numeric for floor_area etc) or fallback to label
                const valueToStore = selectedValue || (selectedOption as any)?.label || (selectedOption as any)?.name || '';
                updateDetail(q.id, valueToStore);
              }}
              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an option</option>
              {q.options.map((opt: any, optIdx: number) => {
                const optValue = (opt as any)?.value || (opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String((opt as any)?.id);
                const optLabel = (opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String((opt as any)?.id);
                return (
                  <option key={`aq_select_${qIdx}_${optIdx}`} value={optValue}>
                    {optLabel}
                  </option>
                );
              })}
            </select>
          )}
          {q.type === 'multi-select' && Array.isArray(q.options) && (
            <div className="mt-2 relative">
              <button
                type="button"
                onClick={() => setOpenMultiSelect(openMultiSelect === q.id ? null : q.id)}
                className="w-full text-left px-3 py-2 border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center"
              >
                <span className="text-gray-700">
                  {Array.isArray(formData.details[q.id]) && formData.details[q.id].length > 0
                    ? `${formData.details[q.id].length} selected`
                    : 'Select options'}
                </span>
                <span className="text-gray-500">▼</span>
              </button>
              {openMultiSelect === q.id && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                  {q.options.map((opt: any, optIdx: number) => {
                    const isChecked = Array.isArray(formData.details[q.id]) && formData.details[q.id].includes(opt.id);
                    return (
                      <label key={`ms_${optIdx}`} className="flex items-start p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={e => {
                            const arr = Array.isArray(formData.details[q.id]) ? [...formData.details[q.id]] : [];
                            if (e.target.checked) {
                              // Store the option NAME (what customer selected) not just the ID
                              arr.push((opt as any)?.name || (opt as any)?.label || (opt as any)?.id);
                            } else {
                              // Remove by name/label, not ID
                              const itemToRemove = (opt as any)?.name || (opt as any)?.label || (opt as any)?.id;
                              const idx = arr.indexOf(itemToRemove);
                              if (idx > -1) arr.splice(idx, 1);
                            }
                            updateDetail(q.id, arr);
                          }}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-sm">{opt.name}</p>
                          <p className="text-xs text-gray-600">{opt.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderQuestionsStep = () => {
    const getQuestions = () => {
      const serviceType = String(formData.service_type);
      const propertyType = String(formData.property_type);
      console.log('\n═══════════════════════════════════════════════════════════════');
      console.log('[QUESTIONS DEBUG - START]');
      console.log(`  property_type: ${propertyType}`);
      console.log(`  service_category: ${formData.service_category}`);
      console.log(`  service_type: ${formData.service_type} (raw) → "${serviceType}" (string)`);
      console.log('═══════════════════════════════════════════════════════════════');
      
      // Core Construction - use dynamic questions
      if (Number(formData.service_category) === 15) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: CORE_CONSTRUCTION (dynamic pricing questions)');
        return coreConstructionQuestions;
      }
      
      // Electrical & Smart Systems - use dynamic questions
      if (Number(formData.service_category) === 17) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: ELECTRICAL (dynamic pricing questions)');
        return electricalQuestions;
      }

      // Landscaping & External Works - use dynamic questions
      if (Number(formData.service_category) === 16) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: LANDSCAPING (dynamic pricing questions)');
        return landscapingQuestions;
      }

      // Finishing & Interiors - use dynamic questions
      if (Number(formData.service_category) === 18) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: FINISHING_INTERIORS (dynamic pricing questions)');
        return finishingInteriorsQuestions;
      }

      // Kitchens & Bathrooms - use dynamic questions
      if (Number(formData.service_category) === 19) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: KITCHENS_BATHROOMS (dynamic pricing questions)');
        return kitchensBathroomsQuestions;
      }

      // Locks & Security - use dynamic questions
      if (Number(formData.service_category) === 20) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: LOCKS_SECURITY (dynamic pricing questions)');
        return locksSecurityQuestions;
      }

      // Plumbing, Heating & Gas - use dynamic questions
      if (Number(formData.service_category) === 21) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: PLUMBING (dynamic pricing questions)');
        return plumbingQuestions;
      }

      // Property Maintenance & Handyman - use dynamic questions
      if (Number(formData.service_category) === 22) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: PROPERTY_MAINTENANCE (dynamic pricing questions)');
        return propertyMaintenanceQuestions;
      }

      // Roofing - use dynamic questions
      if (Number(formData.service_category) === 23) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: ROOFING (dynamic pricing questions)');
        return roofingQuestions;
      }

      // Specialist & Compliance - use dynamic questions
      if (Number(formData.service_category) === 24) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: SPECIALIST_COMPLIANCE (dynamic pricing questions)');
        return specialistComplianceQuestions;
      }

      // Waste & Clearance - use dynamic questions
      if (Number(formData.service_category) === 25) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: WASTE_CLEARANCE (dynamic pricing questions)');
        return wasteClearanceQuestions;
      }

      // Windows, Doors & Glazing - use dynamic questions
      if (Number(formData.service_category) === 26) {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: WINDOWS_DOORS_GLAZING (dynamic pricing questions)');
        return dynamicQuestions; // Will be populated from API
      }
      
      if (propertyType === 'event') {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: EVENT_VENUE_QUESTIONS');
        return EVENT_VENUE_QUESTIONS;
      }
      if (propertyType === 'hmo') {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: HMO_QUESTIONS');
        return HMO_QUESTIONS;
      }
      if (propertyType === 'residential') {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: RESIDENTIAL_QUESTIONS');
        return RESIDENTIAL_QUESTIONS;
      }
      if (propertyType === 'specialist') {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: SPECIALIST_CLEANING_QUESTIONS');
        return SPECIALIST_CLEANING_QUESTIONS;
      }
      if (propertyType === 'commercial') {
        console.log('[QUESTIONS DEBUG] ✅ MATCH: COMMERCIAL_QUESTIONS');
        return COMMERCIAL_QUESTIONS;
      }
      // Bespoke Joinery (Service Type 9) - check both string and number
      if (propertyType === 'carpentry') {
        console.log(`  [CARPENTRY PATH] Checking service_type... Returning ${carpentryQuestions.length} questions from database`);
        return carpentryQuestions;
      }
      console.log('[QUESTIONS DEBUG] ❌ NO MATCH: Using dynamicQuestions');
      console.log(`  Returning ${dynamicQuestions.length} questions`);
      return dynamicQuestions;
    };

    const questions = getQuestions();
    console.log('[QUESTIONS RENDER]');
    console.log(`  Total questions: ${questions.length}`);
    console.log(`  Property type: ${formData.property_type}`);
    console.log(`  Service type: ${formData.service_type}`);
    if (questions.length > 0) {
      console.log(`  First question: "${questions[0].label}"`);
      console.log(`  Last question: "${questions[questions.length - 1].label}"`);
    }
    console.log('═══════════════════════════════════════════════════════════════\n');
    const isCommercial = formData.property_type === 'commercial';

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {formData.property_type === 'event' ? 'Event Venue Details' : 'Property & Service Details'}
          </h2>
          <p className="text-gray-600">Please answer the following questions to generate an accurate quote</p>
        </div>
        
        {isLoadingQuestions && (
          <div className="bg-blue-50 p-6 rounded-lg text-center">
            <p className="text-blue-900 font-semibold">Loading questions...</p>
          </div>
        )}
        
        {!isLoadingQuestions && questions.length > 0 && (
          <div className={`p-6 rounded-lg ${isCommercial ? 'bg-gray-50' : 'bg-blue-50'}`}>
            <h3 className="font-semibold text-gray-900 mb-4">Questions ({questions.length})</h3>
            {questions.map((q, qIdx) => {
              let shouldShow = true;
              if (q.conditional) {
                const [field, value] = q.conditional.split(':');
                if (field === 'service_type') {
                  // Support both exact matches and partial matches (e.g., 'after-builders' matches 'after-builders-residential')
                  shouldShow = formData.service_type === value || formData.service_type?.includes(value);
                }
              }
              if (!shouldShow) return null;
              
              // ✅ FIX: Use semantic field name (q.field_name) if available, fallback to q.id
              // This prevents numeric IDs from appearing in the form
              const fieldName = q.field_name || q.id;
              const displayLabel = typeof q.label === 'string' ? q.label : `Question ${qIdx + 1}`;
              
              return (
                <div key={`q_${qIdx}_${fieldName}`} className="mb-6">
                  <Label className="font-semibold text-gray-900">
                    {displayLabel} {q.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  
                  {q.type === 'text' && (
                    <Input
                      value={formData.details[fieldName] || ''}
                      onChange={e => updateDetail(fieldName, e.target.value)}
                      className="mt-2"
                      placeholder={q.placeholder || "Enter text"}
                    />
                  )}
                  
                  {q.type === 'number' && (
                    <Input
                      type="number"
                      value={formData.details[fieldName] || ''}
                      onChange={e => updateDetail(fieldName, e.target.value)}
                      className="mt-2"
                      placeholder={q.placeholder || "Enter a number"}
                      min="0"
                      step="1"
                    />
                  )}
                  
                  {q.type === 'select' && Array.isArray(q.options) && (
                    <select
                      value={formData.details[fieldName] || ''}
                      onChange={e => {
                        const selectedValue = e.target.value;
                        // Find the option by matching the value (numeric for floor_area etc)
                        const selectedOption = q.options.find((opt: any) => {
                          const optValue = typeof opt === 'string' ? opt : ((opt as any)?.value || (opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String((opt as any)?.id));
                          return optValue === selectedValue;
                        });
                        console.log(`[SELECT CHANGE] Question: ${displayLabel} | Field: ${fieldName} | Selected Value: "${selectedValue}" | Selected ID: "${selectedOption?.id || 'N/A'}"`);
                        if (fieldName === 'frequency') {
                          console.log(`  🎯 FREQUENCY SELECT: ${selectedValue} (ID: ${selectedOption?.id})`);
                          console.log(`  📝 Current state before update:`, formData.details.frequency);
                        }
                        // Store the option VALUE (numeric for floor_area, string for others)
                        // This ensures numeric values like '100' are stored for pricing calculations
                        const valueToStore = selectedValue || (selectedOption as any)?.label || '';
                        console.log(`[SELECT STORE] Storing: "${valueToStore}" for field "${fieldName}"`);
                        updateDetail(fieldName, valueToStore);
                      }}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select an option</option>
                      {q.options.map((opt: any, optIdx: number) => {
                        const optValue = typeof opt === 'string' ? opt : ((opt as any)?.value || (opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String((opt as any)?.id));
                        const optLabel = typeof opt === 'string' ? opt : ((opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String((opt as any)?.id));
                        return (
                          <option key={`opt_${qIdx}_${optIdx}`} value={optValue}>
                            {optLabel}
                          </option>
                        );
                      })}
                    </select>
                  )}
                  
                  {q.type === 'multi-select' && Array.isArray(q.options) && (
                    <div className="mt-2 space-y-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                      {q.options.map((opt: any, optIdx: number) => {
                        const optValue = typeof opt === 'string' ? opt : ((opt as any)?.id || (opt as any)?.value);
                        const optLabel = typeof opt === 'string' ? opt : ((opt as any)?.label || (opt as any)?.name || (opt as any)?.levels || String(optValue));
                        const isChecked = Array.isArray(formData.details[fieldName]) && formData.details[fieldName].includes(optLabel || optValue);
                        return (
                          <label key={`msel_${qIdx}_${optIdx}`} className="flex items-center cursor-pointer p-2 hover:bg-gray-100 rounded">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={e => {
                                const currentArray = Array.isArray(formData.details[fieldName]) ? formData.details[fieldName] : [];
                                // Store the option LABEL (what customer selected) not just the ID
                                if (e.target.checked) {
                                  updateDetail(fieldName, [...currentArray, optLabel || optValue]);
                                } else {
                                  updateDetail(fieldName, currentArray.filter((v: string) => v !== (optLabel || optValue)));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            />
                            <span className="ml-3 text-gray-900 font-medium">{optLabel}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  
                  {q.type === 'textarea' && (
                    <textarea
                      value={formData.details[fieldName] || ''}
                      onChange={e => updateDetail(fieldName, e.target.value)}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={q.placeholder || "Enter your details"}
                      rows={4}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {!isLoadingQuestions && questions.length === 0 && (
          <div className="bg-red-50 border-2 border-red-200 p-6 rounded-lg">
            <p className="text-red-900 font-semibold text-lg">⚠️ No questions available</p>
            <p className="text-red-700 mt-2">
              property_type: <strong>{formData.property_type}</strong> | 
              service_type: <strong>{formData.service_type}</strong>
            </p>
            <p className="text-red-700 text-sm mt-2">Expected Bespoke Joinery to show 12 questions. Check browser console for debugging.</p>
            <button 
              onClick={() => {
                console.log('[DEBUG CLICK] Current formData:', formData);
                console.log('[DEBUG CLICK] BESPOKE_JOINERY_QUESTIONS length:', BESPOKE_JOINERY_QUESTIONS.length);
              }}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Click to debug
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPostcodeStep = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">What is Your Postcode?</h2>
        <p className="text-gray-600">We need your postcode to provide an accurate quote</p>
      </div>
      <div>
        <Label className="font-semibold text-gray-900">Postcode *</Label>
        <Input
          value={formData.postcode}
          onChange={e => updateField('postcode', e.target.value)}
          className="mt-2"
          placeholder="SW1A 1AA"
          required
        />
      </div>
    </div>
  );

  const renderContactStep = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Contact Information</h2>
        <p className="text-gray-600">Please provide your details for the quote</p>
      </div>
      <div>
        <Label className="font-semibold text-gray-900">Name *</Label>
        <Input
          value={formData.contact_name}
          onChange={e => updateField('contact_name', e.target.value)}
          className="mt-2"
          placeholder="Your full name"
          required
        />
      </div>
      <div>
        <Label className="font-semibold text-gray-900">Email *</Label>
        <Input
          type="email"
          value={formData.contact_email}
          onChange={e => updateField('contact_email', e.target.value)}
          className="mt-2"
          placeholder="your@email.com"
          required
        />
      </div>
      <div>
        <Label className="font-semibold text-gray-900">Phone *</Label>
        <Input
          value={formData.contact_phone}
          onChange={e => updateField('contact_phone', e.target.value)}
          className="mt-2"
          placeholder="020 1234 5678"
          required
        />
      </div>
      <div>
        <Label className="font-semibold text-gray-900">Company</Label>
        <Input
          value={formData.company}
          onChange={e => updateField('company', e.target.value)}
          className="mt-2"
          placeholder="Company name (optional)"
        />
      </div>
    </div>
  );

  const getNextStep = (): string | null => {
    const isCleaning = Number(formData.service_category) === 6 || Number(formData.service_category) === 1;
    const isCarpentry = Number(formData.service_category) === 14;
    const isCoreConstruction = Number(formData.service_category) === 15;
    const isLandscaping = Number(formData.service_category) === 16;
    const isElectrical = Number(formData.service_category) === 17;
    const isFinishingInteriors = Number(formData.service_category) === 18;
    const isKitchensBathrooms = Number(formData.service_category) === 19;
    const isLocksSecurityCategory = Number(formData.service_category) === 20;
    const isPlumbingCategory = Number(formData.service_category) === 21;
    const isPropertyMaintenanceCategory = Number(formData.service_category) === 22;
    const isRoofingCategory = Number(formData.service_category) === 23;
    const isSpecialistCompliance = Number(formData.service_category) === 24;
    const isWasteClearance = Number(formData.service_category) === 25;
    const isWindowsDoorsGlazing = Number(formData.service_category) === 26;
    const isAirbnb = formData.property_type === 'airbnb';
    const isCommercial = formData.property_type === 'commercial';
    const isEvent = formData.property_type === 'event';
    const isHmo = formData.property_type === 'hmo';
    const isSpecialist = formData.property_type === 'specialist';

    if (step === 'category') return 'serviceType';
    if (step === 'cleaningCategoryStep' && formData.service_type) return 'questions';
    if (step === 'serviceType') {
      if (isCarpentry) return 'carpentryServiceType';
      if (isCoreConstruction) return 'coreConstructionServiceType';
      if (isElectrical) return 'electricalServiceType';
      if (isLandscaping) return 'landscapingServiceType';
      if (isFinishingInteriors) return 'finishingInteriorsServiceType';
      if (isKitchensBathrooms) return 'kitchensBathroomsServiceType';
      if (isLocksSecurityCategory) return 'locksSecurityServiceType';
      if (isPlumbingCategory) return 'plumbingServiceType';
      if (isPropertyMaintenanceCategory) return 'propertyMaintenanceServiceType';
      if (isRoofingCategory) return 'roofingServiceType';
      if (isSpecialistCompliance) return 'specialistComplianceServiceType';
      if (isWasteClearance) return 'wasteClearanceServiceType';
      if (isWindowsDoorsGlazing) return 'windowsDoorsGlazingServiceType';
      if (isCleaning) {
        if (isAirbnb) return 'airbnbService';
        if (isCommercial) return 'commercialServiceType';
        if (isEvent) return 'eventVenueServiceType';
        if (isHmo) return 'hmoServiceType';
        if (isSpecialist) return 'specialistCleaningServiceType';
        return 'cleaningServiceType';
      }
    }
    if (step === 'carpentryServiceType') {
      return 'questions';
    }
    if (step === 'coreConstructionServiceType') {
      return 'coreConstructionQuestions';
    }
    if (step === 'electricalServiceType') {
      return 'electricalQuestions';
    }
    if (step === 'landscapingServiceType') {
      return 'landscapingQuestions';
    }
    if (step === 'finishingInteriorsServiceType') {
      return 'finishingInteriorsQuestions';
    }
    if (step === 'kitchensBathroomsServiceType') {
      return 'kitchensBathroomsQuestions';
    }
    if (step === 'locksSecurityServiceType') {
      return 'locksSecurityQuestions';
    }
    if (step === 'plumbingServiceType') {
      return 'plumbingQuestions';
    }
    if (step === 'propertyMaintenanceServiceType') {
      return 'propertyMaintenanceQuestions';
    }
    if (step === 'roofingServiceType') {
      return 'roofingQuestions';
    }
    if (step === 'specialistComplianceServiceType') {
      return 'specialistComplianceQuestions';
    }
    if (step === 'wasteClearanceServiceType') {
      return 'wasteClearanceQuestions';
    }
    if (step === 'windowsDoorsGlazingServiceType') {
      return 'windowsDoorsGlazingQuestions';
    }
    if (step === 'airbnbService') {
      const selectedAirbnb = AIRBNB_SERVICE_TYPES.find(a => a.id === Number(formData.airbnb_service_type));
      return selectedAirbnb?.id === 6 ? 'hotelHousekeepingMessage' : 'airbnbDetails';
    }
    if (step === 'specialistCleaningServiceType') {
      return 'specialistUnavailable';
    }
    if (['airbnbDetails', 'commercialServiceType', 'eventVenueServiceType', 'hmoServiceType'].includes(step)) {
      return 'questions';
    }
    if (step === 'cleaningServiceType' && formData.service_type) {
      return 'questions';
    }
    if (['questions', 'coreConstructionQuestions', 'electricalQuestions', 'landscapingQuestions', 'finishingInteriorsQuestions', 'kitchensBathroomsQuestions', 'locksSecurityQuestions', 'plumbingQuestions', 'propertyMaintenanceQuestions', 'roofingQuestions', 'specialistComplianceQuestions', 'wasteClearanceQuestions', 'windowsDoorsGlazingQuestions'].includes(step)) return 'postcode';
    if (step === 'postcode') {
      if (!formData.postcode?.trim()) {
        toast({ title: 'Error', description: 'Please enter your postcode', variant: 'destructive' });
        return null;
      }
      return 'contact';
    }
    return null;
  };

  if (isSubmitted && submissionData) {
    return (
      <DashboardLayout>
        <Helmet><title>Service Request Submitted</title></Helmet>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <Card className="border-green-200 bg-green-50">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4"><Check className="h-16 w-16 text-green-600" /></div>
              <CardTitle className="text-2xl text-green-900">Request Submitted Successfully!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-gray-700 mb-2">Your service request has been received and submitted.</p>
                <p className="font-bold text-lg">Request ID: <span className="text-green-600">{submissionData.request_id}</span></p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
                <p className="text-blue-900 font-semibold">Your quote has been submitted successfully.</p>
              </div>
              
              <div className="flex gap-4">
                <Button onClick={() => navigate('/dashboard')} className="flex-1">Go to Dashboard</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet><title>Request a Service</title></Helmet>
      <div className="max-w-2xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="space-y-6 pt-6">
            <div style={{backgroundColor: '#f0f0f0', padding: '8px', marginBottom: '16px', fontSize: '12px', fontFamily: 'monospace', display: 'none'}}>
              DEBUG: step={step} | category={formData.service_category} | serviceTypes.length={serviceTypes.length}
            </div>
            {isLoadingData && step === 'category' && (
              <div className="flex justify-center items-center py-12">
                <Loader className="h-8 w-8 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-600">Loading service categories...</span>
              </div>
            )}
            
            {!isLoadingData && step === 'category' && renderCategoryStep()}
            {step === 'serviceType' && renderServiceTypeStep()}
            {step === 'coreConstructionServiceType' && renderCoreConstructionServiceTypeStep()}
            {step === 'coreConstructionQuestions' && renderQuestionsStep()}
            {step === 'electricalServiceType' && renderElectricalServiceTypeStep()}
            {step === 'landscapingServiceType' && renderLandscapingServiceTypeStep()}
            {step === 'finishingInteriorsServiceType' && renderFinishingInteriorsServiceTypeStep()}
            {step === 'kitchensBathroomsServiceType' && renderKitchensBathroomsServiceTypeStep()}
            {step === 'locksSecurityServiceType' && renderLocksSecurityServiceTypeStep()}
            {step === 'plumbingServiceType' && renderPlumbingServiceTypeStep()}
            {step === 'propertyMaintenanceServiceType' && renderPropertyMaintenanceServiceTypeStep()}
            {step === 'roofingServiceType' && renderRoofingServiceTypeStep()}
            {step === 'specialistComplianceServiceType' && renderSpecialistComplianceServiceType()}
            {step === 'wasteClearanceServiceType' && renderWasteClearanceServiceType()}
            {step === 'windowsDoorsGlazingServiceType' && renderWindowsDoorsGlazingServiceType()}
            {step === 'electricalQuestions' && renderQuestionsStep()}
            {step === 'landscapingQuestions' && renderQuestionsStep()}
            {step === 'finishingInteriorsQuestions' && renderQuestionsStep()}
            {step === 'kitchensBathroomsQuestions' && renderQuestionsStep()}
            {step === 'locksSecurityQuestions' && renderQuestionsStep()}
            {step === 'plumbingQuestions' && renderQuestionsStep()}
            {step === 'propertyMaintenanceQuestions' && renderQuestionsStep()}
            {step === 'roofingQuestions' && renderQuestionsStep()}
            {step === 'specialistComplianceQuestions' && renderQuestionsStep()}
            {step === 'wasteClearanceQuestions' && renderQuestionsStep()}
            {step === 'windowsDoorsGlazingQuestions' && renderQuestionsStep()}
            {step === 'airbnbService' && renderAirbnbServiceStep()}
            {step === 'commercialServiceType' && renderCommercialServiceTypeStep()}
            {step === 'eventVenueServiceType' && renderEventVenueServiceTypeStep()}
            {step === 'hmoServiceType' && renderHmoServiceTypeStep()}
            {step === 'specialistCleaningServiceType' && renderSpecialistCleaningServiceTypeStep()}
            {step === 'cleaningCategoryStep' && renderCleaningServiceCategoryStep()}
            {step === 'cleaningServiceType' && renderCleaningServiceTypeStep()}
            {step === 'carpentryServiceType' && renderCarpentryServiceTypeStep()}
            {step === 'hotelHousekeepingMessage' && renderHotelHousekeepingMessageStep()}
            {step === 'specialistUnavailable' && renderSpecialistUnavailableStep()}
            {step === 'airbnbDetails' && renderAirbnbDetailsStep()}
            {step === 'questions' && renderQuestionsStep()}
            {step === 'postcode' && renderPostcodeStep()}
            {step === 'contact' && renderContactStep()}
            
            <div className="flex gap-4 pt-6">
              {step !== 'category' && step !== 'hotelHousekeepingMessage' && step !== 'specialistUnavailable' && (
                <Button onClick={handleGoBack} variant="outline" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
              
              {step === 'specialistUnavailable' && (
                <Button onClick={handleGoBack} variant="outline" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
              
              {step !== 'contact' && 
                step !== 'hotelHousekeepingMessage' && 
                step !== 'specialistUnavailable' && (
                <Button
                  onClick={() => {
                    // Validate required questions before proceeding
                    const questionSteps = ['questions', 'coreConstructionQuestions', 'electricalQuestions', 'landscapingQuestions', 'finishingInteriorsQuestions', 'kitchensBathroomsQuestions', 'locksSecurityQuestions', 'plumbingQuestions', 'propertyMaintenanceQuestions', 'roofingQuestions', 'specialistComplianceQuestions', 'wasteClearanceQuestions', 'windowsDoorsGlazingQuestions'];
                    
                    if (questionSteps.includes(step)) {
                      if (!validateRequiredQuestions()) {
                        return; // Stay on current step if validation fails
                      }
                    }
                    
                    const nextStep = getNextStep();
                    if (nextStep) setStep(nextStep);
                  }}
                  className="ml-auto"
                >
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              
              {step === 'contact' && (
                <Button onClick={handleSubmit} disabled={isSubmitting} className="ml-auto bg-green-600 hover:bg-green-700">
                  <Check className="h-4 w-4" /> {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ServiceRequestPage;